"use client";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/HoverCard";
import { useArchivalMode } from "@/contexts/ArchivalModeContext";
import { useAuth } from "@/contexts/AuthContext";
import { useEngagement } from "@/contexts/EngagementContext";
import { useExecutionEngagementDetails } from "@/contexts/ExecutionEngagementDetailsContext";
import { getCompanyName } from "@/lib/textron-data-processor";
import { apiService } from "@/services/api-service";
import {
  Activity,
  AlertCircle,
  AlertTriangle,
  ArrowLeft,
  BarChart3,
  Book,
  BookOpen,
  Boxes,
  Building,
  Building2,
  Calculator,
  ChartBar,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  ClipboardCheck,
  ClipboardList,
  CreditCard,
  Database,
  DollarSign,
  FileCheck,
  FileCode,
  FileText,
  Filter,
  Flag,
  Gavel,
  Heart,
  Home,
  Layers,
  LineChart,
  LayoutDashboard,
  Mail,
  MessageSquare,
  Monitor,
  Network,
  Play,
  Plus,
  Search,
  Settings,
  Shield,
  Share2,
  Target,
  TrendingUp,
  Users,
  Workflow,
  XCircle,
} from "lucide-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  FRF_AS,
  FRF_IND_AS,
  getStandardProceduresForWorkspace,
} from "../constants/standard-procedures";
import {
  getProcedureRouteId as getProcedureRouteIdFromUtils,
  getWorkspaceFromProcedureRoute,
} from "../utils/procedure-route-mapping";
import {
  getExecutionSectionFromWorkspace,
  getWorkspaceFromSubstantivePrefix,
} from "../utils/substantive-procedures-mapping";
import { isLibraryHiddenForLimitedReview } from "../utils/execution-limited-review-ui";
import { loadSubstantiveProcedures } from "../utils/substantive-procedures-utils";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Input } from "./ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";

interface LeftPanelProps {
  activeSection: string;
  setActiveSection: (section: string) => void;
}

// Standard workspaces definition
interface StandardWorkspace {
  name: string;
  prefix: string;
  area: "Planning" | "Execution" | "ConcludingProcedures";
}

function isWorkspaceAllowedForFrf(workspaceName: string, frf: string | null): boolean {
  if (!frf) return true;

  if (frf === FRF_IND_AS) {
    return !["Lease Liability", "Borrowings", "Other Receivables"].includes(
      workspaceName,
    );
  }

  if (frf === FRF_AS) {
    return ![
      "Biological Assets other than Bearer Plants",
      "Lease Liability - Ind AS",
      "Right-of-Use Assets",
      "Borrowings - Ind AS",
      "Other Financial Liabilities",
      "Other Financial Assets",
    ].includes(workspaceName);
  }

  return true;
}

function shouldHideCaroFromConcludingStandardList(
  workspace: StandardWorkspace,
  selectedArea: StandardWorkspace["area"],
  isLimitedReviewExecution: boolean,
): boolean {
  return (
    isLimitedReviewExecution &&
    selectedArea === "ConcludingProcedures" &&
    workspace.area === "ConcludingProcedures" &&
    workspace.name === "CARO"
  );
}

const STANDARD_WORKSPACES: StandardWorkspace[] = [
  // Planning
  {
    name: "Engagement Acceptance & Continuance",
    prefix: "EAC",
    area: "Planning",
  },
  {
    name: "Understanding the Entity & its Environment",
    prefix: "UEE",
    area: "Planning",
  },
  { name: "Materiality", prefix: "MAT", area: "Planning" },
  {
    name: "Preliminary Analytical Procedures",
    prefix: "PAP",
    area: "Planning",
  },
  { name: "Fraud Risk Assessment", prefix: "FRA", area: "Planning" },
  { name: "IT Risk Assessment", prefix: "ITRA", area: "Planning" },

  { name: "Independence", prefix: "IND", area: "Planning" },

  // Execution
  { name: "Employee Benefits Expense", prefix: "EBE", area: "Execution" },
  { name: "Lease Liability", prefix: "LEASE", area: "Execution" },
  { name: "Lease Liability - Ind AS", prefix: "LEASE", area: "Execution" },
  { name: "Right-of-Use Assets", prefix: "ROU", area: "Execution" },
  {
    name: "Provisions & Contingent Liabilities",
    prefix: "PCL",
    area: "Execution",
  },
  { name: "Trade Payables", prefix: "TP", area: "Execution" },
  { name: "Inventory", prefix: "INV", area: "Execution" },
  { name: "Cash & Cash Equivalents", prefix: "CCE", area: "Execution" },
  { name: "Finance Cost", prefix: "FINCO", area: "Execution" },
  { name: "Income Tax", prefix: "ITAX", area: "Execution" },
  { name: "Investments", prefix: "INVST", area: "Execution" },
  { name: "Other Assets", prefix: "OTAS", area: "Execution" },
  { name: "Other Financial Assets", prefix: "OFA", area: "Execution" },
  { name: "Other Income", prefix: "OTINC", area: "Execution" },
  { name: "Revenue from Operations", prefix: "RFO", area: "Execution" },
  { name: "Property, Plant & Equipment", prefix: "PPE", area: "Execution" },
  { name: "Other Liabilities", prefix: "OTL", area: "Execution" },
  { name: "Other Financial Liabilities", prefix: "OFL", area: "Execution" },
  { name: "Other Receivables", prefix: "OTR", area: "Execution" },
  { name: "Opening Balance Testing", prefix: "OBT", area: "Execution" },
  { name: "Related Party Disclosure", prefix: "RPT", area: "Execution" },
  { name: "Equity", prefix: "EQU", area: "Execution" },
  { name: "Management Use of Expert", prefix: "MUE", area: "Execution" },
  { name: "Intangible Assets", prefix: "IT", area: "Execution" },
  { name: "Depreciation", prefix: "DEPR", area: "Execution" },
  { name: "Impairment Testing", prefix: "IMPR", area: "Execution" },
  { name: "Trade Receivables", prefix: "TR", area: "Execution" },
  { name: "Other Expenses", prefix: "OTEX", area: "Execution" },
  { name: "Borrowings", prefix: "DEBT", area: "Execution" },
  { name: "Borrowings - Ind AS", prefix: "DEBT", area: "Execution" },
  { name: "Cost of Materials Consumed", prefix: "COGS", area: "Execution" },
  {
    name: "Financial Closing & Reporting Process",
    prefix: "FCRP",
    area: "Execution",
  },
  { name: "Management Override of Controls", prefix: "MOC", area: "Execution" },
  {
    name: "Biological Assets other than Bearer Plants",
    prefix: "BAPB",
    area: "Execution",
  },

  // ConcludingProcedures
  { name: "CARO", prefix: "CARO", area: "ConcludingProcedures" },
  {
    name: "Audit Report & Opinion",
    prefix: "AUDREP",
    area: "ConcludingProcedures",
  },
  {
    name: "Communication with those charged with governance",
    prefix: "CTCWG",
    area: "ConcludingProcedures",
  },
  { name: "Subsequent Events", prefix: "SE", area: "ConcludingProcedures" },
  {
    name: "Obtain Written Representations",
    prefix: "WR",
    area: "ConcludingProcedures",
  },
  { name: "Litigations & Claims", prefix: "LIT", area: "ConcludingProcedures" },
  {
    name: "Compliance with laws and regulations",
    prefix: "CLR",
    area: "ConcludingProcedures",
  },
  {
    name: "Review of estimates for management bias",
    prefix: "REMB",
    area: "ConcludingProcedures",
  },
  { name: "Internal Audit", prefix: "ITAUD", area: "ConcludingProcedures" },
  { name: "Going Concern", prefix: "GC", area: "ConcludingProcedures" },
  {
    name: "Evaluate Audit Results Including Misstatements",
    prefix: "EARM",
    area: "ConcludingProcedures",
  },
];

const OPENING_BALANCE_TESTING_WORKSPACE = "Opening Balance Testing";
const OPENING_BALANCE_TESTING_SEED_XLSX = "Opening Balance Testing.xlsx";

// Main icon categories in the icon strip
const mainCategories = [
  {
    id: "partner-review",
    title: "Partner Review",
    icon: Shield,
    description: "Partner review dashboard",
  },
  {
    id: "eqr-review",
    title: "EQR Review",
    icon: Gavel,
    description: "EQR review dashboard",
  },
  {
    id: "engagement-overview",
    title: "Engagement Overview ",
    icon: ChartBar,
    description: "Engagement overview",
  },
  {
    id: "planning-overview",
    title: "Planning ",
    icon: ClipboardList,
    description: "Planning phase ",
  },
  {
    id: "execution",
    title: "Execution",
    icon: Play,
    description: "Audit execution phase",
  },
  {
    id: "concluding",
    title: "Concluding Procedures",
    icon: CheckCircle,
    description: "Final audit procedures",
  },
  {
    id: "financial-data",
    title: "Financial Data",
    icon: Database,
    description: "Financial analysis & reports",
  },
  {
    id: "libraries",
    title: "Libraries",
    icon: Book,
    description: "Access various libraries",
  },
];

// Financial data menu items (your existing menu)
const financialMenuItems = [
  { id: "overview", title: "Overview", icon: Home, description: "Key metrics" },
  {
    id: "financial-ratios",
    title: "Financial Ratios",
    icon: Calculator,
    description: "YoY Ratio Analysis",
  },
  {
    id: "balance-sheet",
    title: "Balance Sheet",
    icon: FileText,
    description: "Assets & Liabilities",
  },
  {
    id: "profit-loss",
    title: "Profit & Loss",
    icon: TrendingUp,
    description: "Income & Expenses",
  },
  {
    id: "trial-balance",
    title: "Trial Balance",
    icon: FileText,
    description: "Account balances",
  },
  {
    id: "audit-entries",
    title: "Audit Entries",
    icon: FileCheck,
    description: "Upload and manage audit entries",
  },
];

// Library items - sorted alphabetically, disabled ones at the end
const libraryItems = [
  {
    id: "confirmation-library",
    title: "Confirmation Library",
    description: "Manage audit confirmations",
    icon: FileCheck,
  },
  {
    id: "sampling-library",
    title: "Sampling Library",
    description: "View sampling documentation and logs",
    icon: ClipboardCheck,
  },
  {
    id: "control-library",
    title: "Control Library",
    description: "Manage and view all internal controls",
    icon: Shield,
  },
  {
    id: "control-owner-library",
    title: "Control Owner Library",
    description: "Control ownership matrix",
    icon: Users,
  },
  {
    id: "deficiency-library",
    title: "Deficiency Library",
    description: "Track audit deficiencies",
    icon: XCircle,
  },
  {
    id: "document-library",
    title: "Document Library",
    description: "Upload and manage documents",
    icon: FileText,
  },
  {
    id: "information-library",
    title: "Information Library",
    description: "Access to information resources",
    icon: BookOpen,
  },
  {
    id: "misstatement-library",
    title: "Misstatement Library",
    description: "Track and manage misstatements",
    icon: AlertCircle,
  },
  {
    id: "rcm-library",
    title: "RCM Library",
    description: "Risk Control Matrix",
    icon: Network,
  },
  {
    id: "review-note-library",
    title: "Review Note Library",
    description: "View all review notes and comments",
    icon: MessageSquare,
  },
  {
    id: "risk-library",
    title: "Risk Library",
    description: "Manage and view all identified risks (RoMMs)",
    icon: AlertTriangle,
  },
  // Disabled libraries at the end
  {
    id: "business-process-library",
    title: "Business Process Library",
    description: "Business process documentation",
    icon: Workflow,
  },
  {
    id: "it-elements-library",
    title: "IT Elements Library",
    description: "IT controls and systems",
    icon: Monitor,
  },
];

export function LeftPanel({ activeSection, setActiveSection }: LeftPanelProps) {
  const { isArchivalMode } = useArchivalMode();
  const { user } = useAuth();
  const { currentEngagement } = useEngagement();
  const { isLimitedReviewExecution } = useExecutionEngagementDetails();
  const visibleLibraryItems = useMemo(
    () =>
      libraryItems.filter(
        (lib) => !isLibraryHiddenForLimitedReview(lib.id, isLimitedReviewExecution),
      ),
    [isLimitedReviewExecution],
  );
  const [canAccessPartnerReview, setCanAccessPartnerReview] = useState(false);
  const [canAccessEqrReview, setCanAccessEqrReview] = useState(false);
  const [searchFilter, setSearchFilter] = useState("");
  const [executionSearchQuery, setExecutionSearchQuery] = useState("");
  const [planningSearchQuery, setPlanningSearchQuery] = useState("");
  const [concludingSearchQuery, setConcludingSearchQuery] = useState("");
  const [substantiveSearchQuery, setSubstantiveSearchQuery] = useState("");
  const [librariesSearchQuery, setLibrariesSearchQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState("financial-data");
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [companyName, setCompanyName] = useState<string>("");
  const [payroll1Answer, setPayroll1Answer] = useState<string | null>(null);
  const [isLoadingPayroll1, setIsLoadingPayroll1] = useState<boolean>(true);
  const [leaseIndASQ4Answer, setLeaseIndASQ4Answer] = useState<string>("");
  const [sectionsJson, setSectionsJson] = useState<any | null>(null);
  const [sectionsError, setSectionsError] = useState<string | null>(null);
  const [engagementFrf, setEngagementFrf] = useState<string | null>(null);
  const [addWorkspaceOpen, setAddWorkspaceOpen] = useState(false);
  const [standardWorkspaceOpen, setStandardWorkspaceOpen] = useState(false);
  const [newWorkspaceName, setNewWorkspaceName] = useState("");
  const [newWorkspaceId, setNewWorkspaceId] = useState("");
  const [newWorkspaceArea, setNewWorkspaceArea] = useState<
    "Planning" | "Execution" | "ConcludingProcedures"
  >("Planning");
  const [isAddingWorkspace, setIsAddingWorkspace] = useState(false);
  const [selectedAreaForStandard, setSelectedAreaForStandard] = useState<
    "Planning" | "Execution" | "ConcludingProcedures"
  >("Planning");
  const [selectedStandardWorkspaces, setSelectedStandardWorkspaces] = useState<
    Set<string>
  >(new Set());
  const [loadedProceduresData, setLoadedProceduresData] = useState<any>(null);
  const [deleteWorkspaceOpen, setDeleteWorkspaceOpen] = useState(false);
  const [deleteWorkspaceArea, setDeleteWorkspaceArea] = useState<
    "Planning" | "Execution" | "ConcludingProcedures"
  >("Planning");
  const [selectedWorkspacesToDelete, setSelectedWorkspacesToDelete] = useState<
    Set<string>
  >(new Set());
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [isDeletingWorkspace, setIsDeletingWorkspace] = useState(false);
  const [isControlAutomated, setIsControlAutomated] = useState<boolean | null>(null);
  
  // Dropdown menu states to ensure they close before opening dialogs
  const [executionDropdownOpen, setExecutionDropdownOpen] = useState(false);
  const [planningDropdownOpen, setPlanningDropdownOpen] = useState(false);
  const [concludingDropdownOpen, setConcludingDropdownOpen] = useState(false);

  useEffect(() => {
    const loadFrf = async () => {
      try {
        const frfResult = await (window as any).sharePointAPI?.getFrf?.();
        const frf = frfResult?.success ? String(frfResult.frf || "").trim() : "";
        setEngagementFrf(frf || null);
      } catch {
        setEngagementFrf(null);
      }
    };

    loadFrf();
  }, [currentEngagement?.id]);

  useEffect(() => {
    if (activeCategory === "utility" || activeCategory === "bin") {
      setActiveCategory("financial-data");
    }
  }, [activeCategory]);

  useEffect(() => {
    if (
      !isLimitedReviewExecution ||
      selectedAreaForStandard !== "ConcludingProcedures"
    ) {
      return;
    }
    setSelectedStandardWorkspaces((prev) => {
      if (!prev.has("CARO")) return prev;
      const next = new Set(prev);
      next.delete("CARO");
      return next;
    });
  }, [isLimitedReviewExecution, selectedAreaForStandard]);

  useEffect(() => {
    if (!isLibraryHiddenForLimitedReview(activeSection, isLimitedReviewExecution)) {
      return;
    }
    setActiveSection("libraries");
  }, [isLimitedReviewExecution, activeSection, setActiveSection]);

  useEffect(() => {
    const loadLeaseIndASTailoring = async () => {
      try {
        const res = await (window as any)?.sharePointAPI?.downloadClientJson?.(
          "Execution_TailoringQuestions_Responses.json",
        );
        const answer =
          res?.data?.["Lease Liability - Ind AS"]?.["lease-ind-as-4"]
            ?.selectedOption || "";
        setLeaseIndASQ4Answer(String(answer));
      } catch {
        setLeaseIndASQ4Answer("");
      }
    };
    loadLeaseIndASTailoring();
  }, [activeSection]);

  // Load control nature to determine if it's Automated
  useEffect(() => {
    const loadControlNature = async () => {
      if (!activeSection.startsWith("internal-control:")) {
        setIsControlAutomated(null);
        return;
      }

      const controlId = activeSection.split(":")[1];
      if (!controlId) {
        setIsControlAutomated(null);
        return;
      }

      try {
        if ((window as any).sharePointAPI?.downloadRcmJson) {
          const rcmResult = await (
            window as any
          ).sharePointAPI.downloadRcmJson();
          if (rcmResult?.success && rcmResult?.data) {
            const librariesRcm =
              rcmResult.data.libraries_RCM ||
              rcmResult.data.libraries_rcm ||
              {};
            const controlEntry = librariesRcm[controlId];
            if (controlEntry && controlEntry.Nature) {
              const nature = String(controlEntry.Nature).toLowerCase();
              setIsControlAutomated(nature.includes("automated"));
            } else {
              setIsControlAutomated(false); // Default to false if not found
            }
          } else {
            setIsControlAutomated(false);
          }
        } else {
          setIsControlAutomated(false);
        }
      } catch (error) {
        console.error("Failed to load control nature:", error);
        setIsControlAutomated(false);
      }
    };

    loadControlNature();
  }, [activeSection]);

  // Update activeCategory when viewing internal control to switch left panel
  useEffect(() => {
    if (activeSection.startsWith("internal-control:")) {
      // Switch to libraries category so the left panel shows control navigation
      setActiveCategory("libraries");
    } else if (activeSection.startsWith("dynamic-workspace-controls:")) {
      // Parse the area from the activeSection to set the correct category
      const parts = activeSection.split(":");
      if (parts.length >= 2) {
        const area = parts[1]; // e.g., "Execution", "Planning", "ConcludingProcedures"
        if (area === "Execution") {
          setActiveCategory("execution");
        } else if (area === "Planning") {
          setActiveCategory("planning-overview");
        } else if (area === "ConcludingProcedures") {
          setActiveCategory("concluding");
        }
      }
    } else if (activeSection.startsWith("dynamic-workspace-romms:")) {
      // Parse the area for ROMMs view
      const parts = activeSection.split(":");
      if (parts.length >= 2) {
        const area = parts[1];
        if (area === "Execution") {
          setActiveCategory("execution");
        } else if (area === "Planning") {
          setActiveCategory("planning-overview");
        } else if (area === "ConcludingProcedures") {
          setActiveCategory("concluding");
        }
      }
    }
  }, [activeSection]);

  // Mock data for quick stats (replace with your actual data)
  const mockMetrics = {
    totalDebits: 1250000,
    totalCredits: 1180000,
    netPosition: 70000,
    profitMargin: 12.5,
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  // Load company name
  useEffect(() => {
    const loadCompanyName = async () => {
      try {
        const name = await getCompanyName("combined");
        setCompanyName(name || "ABC Private Limited");

      } catch (error) {
        console.warn("Failed to load company name, using fallback");
        setCompanyName("");
      }
    };
    loadCompanyName();
  }, []);

  // Partner Review icon visible only for engagement_partner (NOT for eqr_partner)
  useEffect(() => {
    const checkPartnerReviewAccess = async () => {
      if (!currentEngagement || !user) {
        setCanAccessPartnerReview(false);
        return;
      }
      try {
        const team = await apiService.getEngagementTeam(currentEngagement.id);
        const userId = String(user.id);
        const normalizedTeam = (team || []).map((member: any) => ({
          id: String(member.id ?? member.user_id ?? member.User?.id ?? ""),
          role:
            member.role ??
            member.EngagementUser?.role ??
            (member as any).engagement_user?.role ??
            member.EngagementUser?.Role?.name ??
            "associate",
        }));
        const currentUserMember = normalizedTeam.find((m) => m.id === userId);
        // Only allow engagement_partner, explicitly exclude eqr_partner and all other roles
        const hasAccess =
          currentUserMember &&
          currentUserMember.role === "engagement_partner" &&
          currentUserMember.role !== "eqr_partner";
        setCanAccessPartnerReview(!!hasAccess);
      } catch {
        setCanAccessPartnerReview(false);
      }
    };
    checkPartnerReviewAccess();
  }, [currentEngagement, user]);

  // EQR Review icon visible only for eqr_manager or eqr_partner
  useEffect(() => {
    const checkEqrReviewAccess = async () => {
      if (!currentEngagement || !user) {
        setCanAccessEqrReview(false);
        return;
      }
      try {
        const team = await apiService.getEngagementTeam(currentEngagement.id);
        const userId = String(user.id);
        const normalizedTeam = (team || []).map((member: any) => ({
          id: String(member.id ?? member.user_id ?? member.User?.id ?? ""),
          role:
            member.role ??
            member.EngagementUser?.role ??
            (member as any).engagement_user?.role ??
            member.EngagementUser?.Role?.name ??
            "associate",
        }));
        const currentUserMember = normalizedTeam.find((m) => m.id === userId);
        const hasAccess =
          currentUserMember &&
          (currentUserMember.role === "eqr_partner" ||
            currentUserMember.role === "eqr_manager");
        setCanAccessEqrReview(!!hasAccess);
      } catch {
        setCanAccessEqrReview(false);
      }
    };
    checkEqrReviewAccess();
  }, [currentEngagement, user]);

  // Partner review subsections are intentionally accessible from the left panel.

  // Load unified Sections.json from SharePoint for Planning / Execution / Concluding menus
  // every time one of those phases is active, so workspaces are always fresh.
  useEffect(() => {
    const shouldLoad =
      activeCategory === "planning-overview" ||
      activeCategory === "execution" ||
      activeCategory === "concluding";

    if (!shouldLoad) {
      return;
    }

    const loadSections = async () => {
      try {
        if (!(window as any).sharePointAPI?.downloadSectionsJson) {
          console.warn(
            "[LeftPanel] sharePointAPI.downloadSectionsJson is not available",
          );
          return;
        }
        console.log(
          "[LeftPanel] Requesting Sections.json from SharePoint for category:",
          activeCategory,
        );
        const res = await (window as any).sharePointAPI.downloadSectionsJson();
        if (res?.success && res.data) {
          console.log("[LeftPanel] Sections.json loaded from SharePoint:", {
            hasPlanning: !!res.data?.Planning,
            hasExecution: !!res.data?.Execution,
            hasConcluding: !!res.data?.ConcludingProcedures,
            planningKeys: res.data?.Planning
              ? Object.keys(res.data.Planning)
              : [],
            executionKeys: res.data?.Execution
              ? Object.keys(res.data.Execution)
              : [],
            concludingKeys: res.data?.ConcludingProcedures
              ? Object.keys(res.data.ConcludingProcedures)
              : [],
          });
          setSectionsJson(res.data);
          // Show warning if file was invalid JSON and we fell back to empty structure
          setSectionsError(res.parseError ?? null);
        } else if (res?.error) {
          console.error(
            "[LeftPanel] Failed to load Sections.json from SharePoint:",
            res.error,
          );
          setSectionsError(res.error);
        }
      } catch (e: any) {
        console.error("[LeftPanel] Exception while loading Sections.json:", e);
        setSectionsError(e?.message || "Failed to load Sections.json");
      }
    };

    loadSections();
  }, [activeCategory]);

  // Ensure activeCategory is set to "engagement-overview" when engagement overview section is active
  useEffect(() => {
    if (!activeSection) return;

    if (
      (activeSection === "engagement-overview" || activeSection === "chat") &&
      activeCategory !== "engagement-overview"
    ) {
      setActiveCategory("engagement-overview");
    }
  }, [activeSection, activeCategory]);

  // Ensure activeCategory is set to "planning-overview" when planning section is active
  useEffect(() => {
    if (!activeSection) return;

    if (
      (activeSection.startsWith("planning") ||
        activeSection === "materiality") &&
      activeCategory !== "planning-overview"
    ) {
      setActiveCategory("planning-overview");
    }
  }, [activeSection, activeCategory]);

  // Ensure activeCategory is set to "concluding" when concluding section is active
  useEffect(() => {
    if (!activeSection) return;

    if (
      (activeSection.startsWith("concluding") || activeSection === "caro") &&
      activeCategory !== "concluding"
    ) {
      setActiveCategory("concluding");
    }
  }, [activeSection, activeCategory]);

  // Ensure activeCategory is set to "execution" when execution section is active
  useEffect(() => {
    if (!activeSection) return;

    // Skip if this is a concluding procedures route (handled by concluding useEffect)
    if (activeSection.startsWith("concluding")) {
      return;
    }

    const isProcedureRoute =
      activeSection.includes("-substantive") ||
      activeSection.startsWith("dynamic-workspace-substantive:") ||
      activeSection.startsWith("custom-procedure:") ||
      getWorkspaceFromProcedureRoute(activeSection) !== null; // Check if it's a procedure route

    const isExecutionSection =
      activeSection.startsWith("execution-") || isProcedureRoute;

    if (isExecutionSection && activeCategory !== "execution") {
      setActiveCategory("execution");
    }
  }, [activeSection, activeCategory]);

  // Track previous section to maintain category context
  const [previousSection, setPreviousSection] = useState<string>("");

  useEffect(() => {
    if (activeSection && activeSection !== previousSection) {
      setPreviousSection(activeSection);
    }
  }, [activeSection, previousSection]);

  // Ensure activeCategory is set to "partner-review" when partner review section is active
  useEffect(() => {
    if (!activeSection) return;
    const isPartnerReviewSection =
      activeSection === "partner-review-dashboard" ||
      activeSection === "partner-review-planning" ||
      activeSection === "partner-review-concluding";

    // If assigned-documents is accessed from a partner-review section, maintain partner-review category
    const isAssignedDocumentsFromPartnerReview =
      activeSection === "assigned-documents" &&
      (previousSection === "partner-review-dashboard" ||
        previousSection === "partner-review-planning" ||
        previousSection === "partner-review-concluding" ||
        activeCategory === "partner-review");

    if (
      (isPartnerReviewSection || isAssignedDocumentsFromPartnerReview) &&
      activeCategory !== "partner-review"
    ) {
      setActiveCategory("partner-review");
    }
  }, [activeSection, activeCategory, previousSection]);

  // Ensure activeCategory is set to "eqr-review" when EQR review section is active
  useEffect(() => {
    if (!activeSection) return;
    const isEqrReviewSection =
      activeSection === "eqr-dashboard" || activeSection === "eqr-checklist";

    // If assigned-documents is accessed from an eqr-review section, maintain eqr-review category
    const isAssignedDocumentsFromEqrReview =
      activeSection === "assigned-documents" &&
      (previousSection === "eqr-dashboard" ||
        previousSection === "eqr-checklist" ||
        activeCategory === "eqr-review");

    if (
      (isEqrReviewSection || isAssignedDocumentsFromEqrReview) &&
      activeCategory !== "eqr-review"
    ) {
      setActiveCategory("eqr-review");
    }
  }, [activeSection, activeCategory, previousSection]);

  // Load procedures from SubstantiveProcedures.json when in substantive procedures mode
  useEffect(() => {
    const loadProcedures = async () => {
      if (!activeSection) {
        setLoadedProceduresData(null);
        return;
      }

      // Check if we're in a substantive procedures view
      // This includes routes with "-substantive", custom procedures, dynamic workspaces,
      // and any route that matches a procedure route pattern
      const isInSubstantiveMode =
        activeSection.includes("-substantive") ||
        activeSection.startsWith("dynamic-workspace-substantive:") ||
        activeSection.startsWith("custom-procedure:") ||
        getWorkspaceFromProcedureRoute(activeSection) !== null;

      if (!isInSubstantiveMode) {
        setLoadedProceduresData(null);
        return;
      }

      try {
        const data = await loadSubstantiveProcedures();
        setLoadedProceduresData(data);
      } catch (error) {
        console.error(
          "Failed to load procedures from SubstantiveProcedures.json",
          error,
        );
        setLoadedProceduresData(null);
      }
    };

    loadProcedures();
  }, [activeSection]);

  // Helper function to get procedure route ID
  // Uses the shared utility function for consistency
  const getProcedureRouteId = (
    workspaceName: string,
    procedureName: string,
    type: "standard" | "custom" | "module",
  ): string => {
    if (type === "custom" || type === "module") {
      return `custom-procedure:${encodeURIComponent(workspaceName)}:${encodeURIComponent(procedureName)}`;
    }

    // Use the shared utility function for standard procedures
    return getProcedureRouteIdFromUtils(workspaceName, procedureName);
  };

  const openAddWorkspaceDialogForArea = (
    area: "Planning" | "Execution" | "ConcludingProcedures",
  ) => {
    setNewWorkspaceArea(area);
    setNewWorkspaceName("");
    setNewWorkspaceId("");
    setAddWorkspaceOpen(true);
  };

  const openStandardWorkspaceDialog = (
    area: "Planning" | "Execution" | "ConcludingProcedures",
  ) => {
    // Close dropdown menus first
    setExecutionDropdownOpen(false);
    setPlanningDropdownOpen(false);
    setConcludingDropdownOpen(false);
    
    // Small delay to ensure dropdown closes before dialog opens
    setTimeout(() => {
      setSelectedAreaForStandard(area);
      setStandardWorkspaceOpen(true);
    }, 100);
  };

  const openCustomWorkspaceDialog = (
    area: "Planning" | "Execution" | "ConcludingProcedures",
  ) => {
    // Close dropdown menus first
    setExecutionDropdownOpen(false);
    setPlanningDropdownOpen(false);
    setConcludingDropdownOpen(false);
    
    // Small delay to ensure dropdown closes before dialog opens
    setTimeout(() => {
      setNewWorkspaceArea(area);
      setNewWorkspaceName("");
      setNewWorkspaceId("");
      setAddWorkspaceOpen(true);
    }, 100);
  };

  const openDeleteWorkspaceDialog = (
    area: "Planning" | "Execution" | "ConcludingProcedures",
  ) => {
    // Close dropdown menus first
    setExecutionDropdownOpen(false);
    setPlanningDropdownOpen(false);
    setConcludingDropdownOpen(false);
    
    // Small delay to ensure dropdown closes before dialog opens
    setTimeout(() => {
      setDeleteWorkspaceArea(area);
      setSelectedWorkspacesToDelete(new Set());
      setDeleteWorkspaceOpen(true);
    }, 100);
  };

  const handleDeleteWorkspaces = async () => {
    if (selectedWorkspacesToDelete.size === 0) {
      alert("Please select at least one workspace to delete");
      return;
    }
    setDeleteConfirmationOpen(true);
  };

  const confirmDeleteWorkspaces = async () => {
    try {
      setIsDeletingWorkspace(true);
      const workspacesToDelete = Array.from(selectedWorkspacesToDelete);

      const res = await (
        window as any
      ).sharePointAPI?.deleteSectionWorkspaces?.({
        workspaceNames: workspacesToDelete,
        area: deleteWorkspaceArea,
      });

      if (res?.success) {
        console.log("[LeftPanel] Workspaces deleted successfully");
        // Update local Sections state
        setSectionsJson((prev: any) => {
          const next = prev && typeof prev === "object" ? { ...prev } : {};
          const areaKey = deleteWorkspaceArea;
          if (next[areaKey] && typeof next[areaKey] === "object") {
            const updatedArea = { ...next[areaKey] };
            workspacesToDelete.forEach((name) => {
              delete updatedArea[name];
            });
            next[areaKey] = updatedArea;
          }
          return next;
        });
        setDeleteConfirmationOpen(false);
        setDeleteWorkspaceOpen(false);
        setSelectedWorkspacesToDelete(new Set());
      } else {
        console.error("[LeftPanel] Failed to delete workspaces", res?.error);
        alert(`Failed to delete workspaces: ${res?.error || "Unknown error"}`);
      }
    } catch (e) {
      console.error("[LeftPanel] Exception while deleting workspaces", e);
      alert(
        `Error deleting workspaces: ${e instanceof Error ? e.message : "Unknown error"}`,
      );
    } finally {
      setIsDeletingWorkspace(false);
    }
  };

  const handleAddStandardWorkspaces = async () => {
    if (selectedStandardWorkspaces.size === 0) {
      alert("Please select at least one workspace to add");
      return;
    }

    try {
      setIsAddingWorkspace(true);

      // Get the selected workspaces
      const workspacesToAdd = STANDARD_WORKSPACES.filter(
        (ws) =>
          ws.area === selectedAreaForStandard &&
          selectedStandardWorkspaces.has(ws.name) &&
          isWorkspaceAllowedForFrf(ws.name, engagementFrf) &&
          !shouldHideCaroFromConcludingStandardList(
            ws,
            selectedAreaForStandard,
            isLimitedReviewExecution,
          ),
      );

      const errors: string[] = [];
      const successes: StandardWorkspace[] = [];

      // Add each workspace
      for (const workspace of workspacesToAdd) {
        try {
          console.log(
            "[LeftPanel] Adding standard workspace via SharePoint API",
            {
              workspaceName: workspace.name,
              workspaceId: workspace.prefix,
              area: workspace.area,
            },
          );
          const res = await (
            window as any
          ).sharePointAPI?.addSectionWorkspace?.({
            workspaceName: workspace.name.trim(),
            workspaceId: workspace.prefix.trim(),
            area: workspace.area,
          });
          if (res?.success) {
            console.log(
              "[LeftPanel] Standard workspace added successfully",
              workspace.name,
            );
            if (workspace.name === OPENING_BALANCE_TESTING_WORKSPACE) {
              const readRes = await window.sharePointAPI?.readScriptsFileBase64?.(
                OPENING_BALANCE_TESTING_SEED_XLSX,
              );
              if (!readRes?.success || !readRes.data?.fileContent) {
                errors.push(
                  `${workspace.name}: Workspace added, but could not load seed file (${readRes?.error || "unknown"}).`,
                );
              } else {
                const uploadRes = await window.sharePointAPI?.uploadFile?.({
                  fileContent: readRes.data.fileContent,
                  fileName: readRes.data.fileName || OPENING_BALANCE_TESTING_SEED_XLSX,
                  referenceValue: OPENING_BALANCE_TESTING_WORKSPACE,
                  folderName: "client",
                  sectionList: ["Execution_OBT_File"],
                });
                if (!uploadRes?.success) {
                  errors.push(
                    `${workspace.name}: Workspace added, but SharePoint upload failed (${uploadRes?.error || "unknown"}).`,
                  );
                }
              }
            }
            successes.push(workspace);
          } else {
            console.error(
              "[LeftPanel] Failed to add standard workspace",
              workspace.name,
              res?.error,
            );
            errors.push(`${workspace.name}: ${res?.error || "Unknown error"}`);
          }
        } catch (e) {
          console.error(
            "[LeftPanel] Exception while adding standard workspace",
            workspace.name,
            e,
          );
          errors.push(
            `${workspace.name}: ${e instanceof Error ? e.message : "Unknown error"}`,
          );
        }
      }

      // Update local state for successful additions
      if (successes.length > 0) {
        setSectionsJson((prev: any) => {
          const next = prev && typeof prev === "object" ? { ...prev } : {};
          const areaKey = selectedAreaForStandard;
          if (!next[areaKey] || typeof next[areaKey] !== "object") {
            next[areaKey] = {};
          }
          successes.forEach((workspace) => {
            next[areaKey] = {
              ...next[areaKey],
              [workspace.name.trim()]: workspace.prefix.trim(),
            };
          });
          return next;
        });
      }

      // Show results
      if (errors.length > 0 && successes.length > 0) {
        alert(
          `Added ${successes.length} workspace(s) successfully. Failed to add ${errors.length} workspace(s):\n${errors.join("\n")}`,
        );
      } else if (errors.length > 0) {
        alert(`Failed to add workspace(s):\n${errors.join("\n")}`);
      } else {
        // All succeeded
        setSelectedStandardWorkspaces(new Set());
        setStandardWorkspaceOpen(false);
      }
    } catch (e) {
      console.error(
        "[LeftPanel] Exception while adding standard workspaces",
        e,
      );
      alert(
        `Error adding workspaces: ${e instanceof Error ? e.message : "Unknown error"}`,
      );
    } finally {
      setIsAddingWorkspace(false);
    }
  };

  const handleAddWorkspaceSubmit = async () => {
    if (!newWorkspaceName.trim() || !newWorkspaceId.trim()) {
      console.warn("[LeftPanel] Workspace Name and ID are required");
      return;
    }
    try {
      setIsAddingWorkspace(true);
      console.log("[LeftPanel] Adding custom workspace via SharePoint API", {
        newWorkspaceName,
        newWorkspaceId,
        newWorkspaceArea,
      });
      const res = await (window as any).sharePointAPI?.addSectionWorkspace?.({
        workspaceName: newWorkspaceName.trim(),
        workspaceId: newWorkspaceId.trim(),
        area: newWorkspaceArea,
      });
      if (res?.success) {
        console.log("[LeftPanel] Custom workspace added successfully");
        // Optimistically update local Sections state
        setSectionsJson((prev: any) => {
          const next = prev && typeof prev === "object" ? { ...prev } : {};
          const areaKey = newWorkspaceArea;
          if (!next[areaKey] || typeof next[areaKey] !== "object") {
            next[areaKey] = {};
          }
          next[areaKey] = {
            ...next[areaKey],
            [newWorkspaceName.trim()]: newWorkspaceId.trim(),
          };
          return next;
        });
        setAddWorkspaceOpen(false);
        setNewWorkspaceName("");
        setNewWorkspaceId("");
      } else {
        console.error("[LeftPanel] Failed to add custom workspace", res?.error);
        alert(`Failed to add workspace: ${res?.error || "Unknown error"}`);
      }
    } catch (e) {
      console.error("[LeftPanel] Exception while adding custom workspace", e);
      alert(
        `Error adding workspace: ${e instanceof Error ? e.message : "Unknown error"}`,
      );
    } finally {
      setIsAddingWorkspace(false);
    }
  };

  // Load Payroll-1 response from SharePoint to determine if Actuary Testing should be shown
  useEffect(() => {
    const checkPayroll1Answer = async () => {
      if (!companyName) {
        console.log("[LeftPanel] Waiting for company name...");
        return;
      }

      console.log(
        "[LeftPanel] Checking Payroll-1 answer for company:",
        companyName,
      );
      setIsLoadingPayroll1(true);

      try {
        if (window.tailoringQuestionsAPI?.loadResponses) {
          const result = await window.tailoringQuestionsAPI.loadResponses({
            companyName,
            areaName: "Employee Benefits Expense",
          });

          console.log("[LeftPanel] Tailoring questions load result:", {
            success: result?.success,
            hasData: !!result?.data,
            dataKeys: result?.data ? Object.keys(result.data) : [],
          });

          if (result.success && result.data) {
            const payroll1Response = result.data["Payroll-1"];
            if (payroll1Response) {
              const answer = (payroll1Response.selectedOption || "").trim();
              setPayroll1Answer(answer);
              console.log(
                "[LeftPanel] Payroll-1 answer loaded:",
                answer,
                "Type:",
                typeof answer,
              );
            } else {
              console.log(
                "[LeftPanel] Payroll-1 response not found in SharePoint",
              );
              setPayroll1Answer(null);
            }
          } else {
            console.log(
              "[LeftPanel] No tailoring questions found in SharePoint",
            );
            setPayroll1Answer(null);
          }
        } else {
          console.warn(
            "[LeftPanel] window.tailoringQuestionsAPI.loadResponses is not available",
          );
          setPayroll1Answer(null);
        }
      } catch (error) {
        console.error("[LeftPanel] Failed to load Payroll-1 response:", error);
        setPayroll1Answer(null);
      } finally {
        setIsLoadingPayroll1(false);
      }
    };

    checkPayroll1Answer();
  }, [companyName]);

  const renderFinancialDataContent = () => (
    <>
      {/* Search & Filter Section */}
      <div className="border-b border-white/10 bg-white/5 p-4 backdrop-blur-sm">
        <h3 className="mb-3 text-sm font-semibold text-white">
          Search & Filter
        </h3>
        <div className="space-y-3">
          <div className="relative">
            <Search className="absolute top-2.5 left-3 h-4 w-4 text-white/40" />
            <Input
              placeholder="Filter reports..."
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
              className="border-white/10 pl-10 text-sm text-white placeholder:text-white/40 focus-visible:border-[#4da3ff]/60 focus-visible:ring-[#4da3ff]/40"
            />
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              className="flex-1 border-white/10 bg-transparent text-xs text-white/80 hover:bg-white/10 hover:text-white"
            >
              <Filter className="mr-1 h-3 w-3" />
              Filter
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="flex-1 border-white/10 bg-transparent text-xs text-white/80 hover:bg-white/10 hover:text-white"
            >
              Export
            </Button>
          </div>
        </div>
      </div>

      {/* Financial Reports Menu */}
      <div className="flex-1 p-4">
        <div className="mb-6">
          <h3 className="mb-4 flex items-center text-sm font-semibold text-white">
            <Building2 className="mr-2 h-4 w-4 text-[#4da3ff]" />
            Financial Reports
          </h3>
          <div className="space-y-1">
            {financialMenuItems.map((item) => {
              const isActive = activeSection === item.id;
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  onClick={() => setActiveSection(item.id)}
                  className={`group flex w-full items-center gap-3 rounded-lg px-3 py-3 text-sm transition-all duration-200 ${
                    isActive
                      ? "border border-[#4da3ff]/30 bg-[#4da3ff]/15 text-white shadow-lg shadow-[#4da3ff]/10"
                      : "text-white/70 hover:bg-white/5 hover:text-white"
                  }`}
                >
                  <Icon
                    className={`h-4 w-4 ${
                      isActive
                        ? "text-[#4da3ff]"
                        : "text-white/50 group-hover:text-white"
                    }`}
                  />
                  <div className="flex-1 text-left">
                    <div className="font-medium">{item.title}</div>
                    <div
                      className={`text-xs ${
                        isActive ? "text-[#4da3ff]/70" : "text-white/50"
                      }`}
                    >
                      {item.description}
                    </div>
                  </div>
                  {isActive && <ChevronRight className="h-4 w-4" />}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );

  // Keep Execution workspace list scrolled to the active workspace
  const executionListContainerRef = useRef<HTMLDivElement | null>(null);
  const executionItemRefs = useRef<Record<string, HTMLButtonElement | null>>(
    {},
  );

  useEffect(() => {
    if (activeCategory !== "execution") return;
    if (!activeSection) return;

    const container = executionListContainerRef.current;
    const target = executionItemRefs.current[activeSection];
    if (!container || !target) return;

    const containerTop = container.scrollTop;
    const containerBottom = containerTop + container.clientHeight;
    const targetTop = target.offsetTop;
    const targetBottom = targetTop + target.offsetHeight;

    // Only adjust scroll if the active item is outside the current viewport
    if (targetTop < containerTop || targetBottom > containerBottom) {
      try {
        target.scrollIntoView({ block: "nearest" });
      } catch {
        // Ignore scroll errors in older browsers/environments
      }
    }
  }, [activeCategory, activeSection]);

  const renderExecutionContent = () => {
    // Mapping of substantive procedure prefixes to their configurations
    const substantiveProceduresMap: Record<
      string,
      {
        executionSection: string;
        title: string;
        procedures: Array<{
          id: string;
          name: string;
          icon: React.ComponentType<{ className?: string }>;
        }>;
      }
    > = {
      "payroll-substantive": {
        executionSection: "execution-payroll",
        title: "Employee Benefits Expense",
        procedures: [
          {
            id: "payroll-substantive-ipe_testing",
            name: "IPE Testing",
            icon: Settings,
          },
          {
            id: "payroll-substantive-exception_testing",
            name: "Exception Testing",
            icon: AlertCircle,
          },
          {
            id: "payroll-substantive-headcount_reconciliation",
            name: "Headcount Reconciliation",
            icon: Users,
          },
          {
            id: "payroll-substantive-mom_analysis",
            name: "M-o-M Analysis",
            icon: TrendingUp,
          },
          {
            id: "payroll-substantive-increment_analysis",
            name: "Increment Analysis",
            icon: Calculator,
          },
          {
            id: "payroll-substantive-salary_analytical",
            name: "Substantive Analytical Procedures",
            icon: DollarSign,
          },
          {
            id: "payroll-substantive-actuary_testing",
            name: "Actuary Testing",
            icon: FileText,
          },
          {
            id: "payroll-substantive-managerial_remuneration",
            name: "Managerial Remuneration",
            icon: Gavel,
          },
          {
            id: "payroll-substantive-share_based_payment",
            name: "Share Based Payment",
            icon: Share2,
          },
        ],
      },
      "ppe-substantive": {
        executionSection: "execution-ppe",
        title: "Property, Plant & Equipment",
        procedures: [
          { id: "ppe-ipe-testing", name: "IPE Testing", icon: FileCheck },
          {
            id: "ppe-exception-testing",
            name: "Exception Testing",
            icon: Shield,
          },
          {
            id: "ppe-additions-deletions",
            name: "Additions & Deletions Analysis",
            icon: TrendingUp,
          },
          { id: "ppe-cwip-analysis", name: "CWIP Analysis", icon: Building },
          {
            id: "ppe-non-current-assets-held-for-sale",
            name: "Non-Current Assets Held for Sale",
            icon: ClipboardList,
          },
        ],
      },
      "ia-substantive": {
        executionSection: "execution-ia",
        title: "Intangible Assets",
        procedures: [
          { id: "ia-ipe-testing", name: "IPE Testing", icon: FileCheck },
          {
            id: "ia-exception-testing",
            name: "Exception Testing",
            icon: AlertCircle,
          },
          {
            id: "ia-additions-deletions",
            name: "Additions & Deletions Listing",
            icon: TrendingUp,
          },
          {
            id: "ia-under-development",
            name: "Intangible Assets Under Development",
            icon: FileCode,
          },
        ],
      },
      "ll-as-substantive": {
        executionSection: "execution-lease-liability",
        title: "Lease Liability",
        procedures: [
          {
            id: "ll-as-lease-assessment",
            name: "Lease Assessment",
            icon: FileCheck,
          },
          {
            id: "ll-as-finance-lease",
            name: "Finance Lease",
            icon: Building2,
          },
          {
            id: "ll-as-operating-lease",
            name: "Operating Lease",
            icon: Building2,
          },
        ],
      },
      "ll-substantive": {
        executionSection: "execution-lease-liability-ind-as",
        title: "Lease Liability - Ind AS",
        procedures: [
          {
            id: "ll-lease-assessment",
            name: "Lease Assessment",
            icon: FileCheck,
          },
          {
            id: "ll-lease-testing",
            name: "Lease Testing",
            icon: Building2,
          },
          {
            id: "ll-lease-reassessment",
            name: "Lease Reassessment",
            icon: CreditCard,
          },
          {
            id: "ll-lease-modification",
            name: "Lease Modification",
            icon: FileText,
          },
          {
            id: "ll-lease-accounting-exception",
            name: "Lease Accounting Exception",
            icon: AlertCircle,
          },
          {
            id: "ll-lease-disclosure",
            name: "Lease Disclosure",
            icon: ClipboardCheck,
          },
        ],
      },
      "rou-substantive": {
        executionSection: "execution-right-of-use-assets",
        title: "Right-of-Use Assets",
        procedures: [
          {
            id: "rou-disclosure",
            name: "ROU Disclosure",
            icon: ClipboardCheck,
          },
        ],
      },
      "comc-substantive": {
        executionSection: "execution-comc",
        title: "Cost of Materials Consumed",
        procedures: [
          {
            id: "comc-substantive-ipe-testing",
            name: "IPE Testing",
            icon: FileCheck,
          },
          {
            id: "comc-substantive-exception-testing",
            name: "Exception Testing",
            icon: AlertCircle,
          },
          {
            id: "comc-substantive-test-of-details",
            name: "Test of Details",
            icon: ClipboardList,
          },
          {
            id: "comc-substantive-analytical-procedures",
            name: "Analytical Procedures",
            icon: BarChart3,
          },
          {
            id: "comc-substantive-predictive-analytics",
            name: "Predictive Analytics",
            icon: LineChart,
          },
          {
            id: "comc-substantive-purchase-analytical-dashboard",
            name: "Purchase Analytical Dashboard",
            icon: LayoutDashboard,
          },
        ],
      },
      "trdpbl-substantive": {
        executionSection: "execution-trade-payables",
        title: "Trade Payables",
        procedures: [
          { id: "trdpbl-ipe-testing", name: "IPE Testing", icon: FileText },
          {
            id: "trdpbl-exception-testing",
            name: "Exception Testing",
            icon: AlertTriangle,
          },
          {
            id: "trdpbl-ratio-analysis",
            name: "Ratio Analysis",
            icon: Calculator,
          },
          { id: "trdpbl-confirmations", name: "Confirmations", icon: Mail },
        ],
      },
      "inv-substantive": {
        executionSection: "execution-inv",
        title: "Inventory",
        procedures: [
          { id: "inv-ipe-testing", name: "IPE Testing", icon: FileCheck },
          {
            id: "inv-exception-testing",
            name: "Exception Testing",
            icon: Shield,
          },
          { id: "inv-nrv-assessment", name: "NRV Assessment", icon: BarChart3 },
          { id: "inv-cost-allocation", name: "Cost Allocation", icon: Layers },
          {
            id: "inv-inventory-ageing",
            name: "Inventory Ageing",
            icon: ClipboardList,
          },
          {
            id: "inv-physical-verification",
            name: "Physical Verification",
            icon: Boxes,
          },
          {
            id: "inv-overhead-working",
            name: "Overhead Working",
            icon: Layers,
          },
        ],
      },
      "othas-substantive": {
        executionSection: "execution-other-assets",
        title: "Other Assets",
        procedures: [
          { id: "othas-ipe-testing", name: "IPE Testing", icon: FileCheck },
          { id: "othas-prepaid", name: "Prepaid Expense", icon: FileText },
          { id: "othas-sec-deposit", name: "Security Deposit", icon: Shield },
        ],
      },
      "ofa-substantive": {
        executionSection: "execution-other-financial-assets",
        title: "Other Financial Assets",
        procedures: [
          { id: "ofa-security-deposits", name: "Security Deposits", icon: Shield },
        ],
      },
      "trdrcv-substantive": {
        executionSection: "execution-trade-receivables",
        title: "Trade Receivables",
        procedures: [
          { id: "trdrcv-ipe-testing", name: "IPE Testing", icon: FileCheck },
          {
            id: "trdrcv-exception-testing",
            name: "Exception Testing",
            icon: AlertCircle,
          },
          {
            id: "trdrcv-ratio-analysis",
            name: "Ratio Analysis",
            icon: Calculator,
          },
          {
            id: "trdrcv-ecl-flow-rate-yoy",
            name: "ECL Flow Rate Analysis – YoY",
            icon: LineChart,
          },
          {
            id: "trdrcv-ecl-flow-rate-qoq",
            name: "ECL Flow Rate Analysis – QoQ",
            icon: LineChart,
          },
        ],
      },
      "depr-substantive": {
        executionSection: "execution-depreciation",
        title: "Depreciation",
        procedures: [
          {
            id: "depr-substantive",
            name: "Depreciation Procedures",
            icon: Calculator,
          },
        ],
      },
      "imp-substantive": {
        executionSection: "execution-impairment",
        title: "Impairment Testing",
        procedures: [],
      },
      "othexp-substantive": {
        executionSection: "execution-other-expenses",
        title: "Other Expenses",
        procedures: [
          {
            id: "othexp-substantive-ipe-testing",
            name: "IPE Testing",
            icon: FileCheck,
          },
          {
            id: "othexp-substantive-analytical-procedures",
            name: "Analytical Procedures",
            icon: BarChart3,
          },
          {
            id: "othexp-substantive-corporate-social-responsibility",
            name: "Corporate Social Responsibility",
            icon: Heart,
          },
        ],
      },
      "bor-substantive": {
        executionSection: "execution-borrowings",
        title: "Borrowings",
        procedures: [
          {
            id: "bor-substantive-repayment-testing",
            name: "Repayment testing",
            icon: CreditCard,
          },
          {
            id: "bor-substantive-covenant-testing",
            name: "Covenant testing",
            icon: Shield,
          },
          {
            id: "bor-substantive-drawing-power",
            name: "Drawing Power",
            icon: FileText,
          },
          {
            id: "bor-substantive-agreement-summary",
            name: "Agreement Summary",
            icon: ClipboardCheck,
          },
          {
            id: "bor-substantive-quarterly-stock-statement",
            name: "Quarterly Stock Statement",
            icon: BarChart3,
          },
          {
            id: "bor-substantive-classification-testing",
            name: "Classification testing",
            icon: CheckCircle,
          },
        ],
      },
      "borindas-substantive": {
        executionSection: "execution-borrowings-ind-as",
        title: "Borrowings - Ind AS",
        procedures: [
          {
            id: "borindas-substantive-dashboard-agreement-summary",
            name: "Dashboard & Agreement Summary",
            icon: ClipboardCheck,
          },
          {
            id: "borindas-substantive-repayment-testing",
            name: "Repayment testing",
            icon: CreditCard,
          },
          {
            id: "borindas-substantive-covenant-testing",
            name: "Covenant testing",
            icon: Shield,
          },
          {
            id: "borindas-substantive-drawing-power",
            name: "Drawing Power",
            icon: FileText,
          },
          {
            id: "borindas-substantive-utilisation-testing",
            name: "Utilisation Testing",
            icon: FileText,
          },
          {
            id: "borindas-substantive-quarterly-stock-statement",
            name: "Quarterly Stock Statement",
            icon: BarChart3,
          },
          {
            id: "borindas-substantive-classification-testing",
            name: "Classification testing",
            icon: CheckCircle,
          },
        ],
      },
      "cce-substantive": {
        executionSection: "execution-cce",
        title: "Cash & Cash Equivalents",
        procedures: [
          {
            id: "cce-substantive-bank-accounts",
            name: "Bank Accounts",
            icon: Building2,
          },
        ],
      },
      "fincst-substantive": {
        executionSection: "execution-fincst",
        title: "Finance Cost",
        procedures: [],
      },
      "inctx-substantive": {
        executionSection: "execution-inctx",
        title: "Income Tax",
        procedures: [],
      },
      "invsmt-substantive": {
        executionSection: "execution-invsmt",
        title: "Investments",
        procedures: [
          {
            id: "invsmt-substantive-investments-procedures",
            name: "Investments Procedures",
            icon: Calculator,
          },
        ],
      },
      "othinc-substantive": {
        executionSection: "execution-other-income",
        title: "Other Income",
        procedures: [
          {
            id: "othinc-net-investment",
            name: "Net Investment in Leases",
            icon: FileText,
          },
          {
            id: "othinc-other-procedures",
            name: "Other Procedures",
            icon: FileText,
          },
        ],
      },
      "rfo-substantive": {
        executionSection: "execution-rfo",
        title: "Revenue from Operations",
        procedures: [
          {
            id: "rfo-substantive-ship-bill-ipe-testing",
            name: "Ship and Bill - IPE Testing",
            icon: FileCheck,
          },
          {
            id: "rfo-substantive-cost-plus-computation",
            name: "Cost Plus - Computation",
            icon: Calculator,
          },
        ],
      },
      "othlib-substantive": {
        executionSection: "execution-other-liabilities",
        title: "Other Liabilities",
        procedures: [
          {
            id: "othlib-adv-cust-completeness",
            name: "Advance from customer - Completeness Check",
            icon: CheckCircle,
          },
          {
            id: "othlib-adv-cust-ageing",
            name: "Advance from customer - Ageing Analysis",
            icon: BarChart3,
          },
          {
            id: "othlib-capex-vendors-completeness",
            name: "Capex Vendors - Completeness Check",
            icon: CheckCircle,
          },
          {
            id: "othlib-capex-vendors-ageing",
            name: "Capex Vendors - Ageing Analysis",
            icon: BarChart3,
          },
        ],
      },
      "ofllib-substantive": {
        executionSection: "execution-other-financial-liabilities",
        title: "Other Financial Liabilities",
        procedures: [
          {
            id: "ofllib-capex-vendors-completeness",
            name: "Capex Vendors - Completeness Check",
            icon: CheckCircle,
          },
        ],
      },
      "othrec-substantive": {
        executionSection: "execution-other-receivables",
        title: "Other Receivables",
        procedures: [
          {
            id: "othrec-adv-suppliers-completeness",
            name: "Advance to Suppliers - Completeness Check",
            icon: CheckCircle,
          },
          {
            id: "othrec-adv-suppliers-ageing",
            name: "Advance to Suppliers - Ageing Analysis",
            icon: BarChart3,
          },
          {
            id: "othrec-capital-advances-completeness",
            name: "Capital Advances - Completeness Check",
            icon: CheckCircle,
          },
          {
            id: "othrec-capital-advances-ageing",
            name: "Capital Advances - Ageing Analysis",
            icon: BarChart3,
          },
        ],
      },
      "pcl-substantive": {
        executionSection: "execution-pcl",
        title: "Provisions & Contingent Liabilities",
        procedures: [
          {
            id: "pcl-substantive-other-provisions",
            name: "Other Provisions",
            icon: FileText,
          },
        ],
      },
      "golib-substantive": {
        executionSection: "execution-going-concern",
        title: "Going Concern",
        procedures: [
          {
            id: "golib-substantive",
            name: "Going Concern Procedures",
            icon: Calculator,
          },
        ],
      },
      "litclm-substantive": {
        executionSection: "execution-litigations-claims",
        title: "Litigations & Claims",
        procedures: [
          {
            id: "litclm-substantive",
            name: "Litigations & Claims Procedures",
            icon: Calculator,
          },
          {
            id: "litclm-substantive-assessment",
            name: "Assessment",
            icon: Calculator,
          },
        ],
      },
      "opnbal-substantive": {
        executionSection: "execution-opening-balance-testing",
        title: "Opening Balance Testing",
        procedures: [
          {
            id: "opnbal-substantive-inquiry-predecessor",
            name: "Inquiry with Predecessor Auditor",
            icon: FileText,
          },
          {
            id: "opnbal-substantive-sa510-assessment",
            name: "SA 510 Assessment",
            icon: ClipboardCheck,
          },
        ],
      },
      "relpard-substantive": {
        executionSection: "execution-related-party-disclosure",
        title: "Related Party Disclosure",
        procedures: [
          { id: "relpard-compliance", name: "Compliance", icon: Shield },
          {
            id: "relpard-arms-length",
            name: "Arms-Length Assessment",
            icon: FileCheck,
          },
        ],
      },
      "eqt-substantive": {
        executionSection: "execution-equity",
        title: "Equity",
        procedures: [],
      },
      "mgtexp-substantive": {
        executionSection: "execution-management-use-of-expert",
        title: "Management Use of Expert",
        procedures: [
          {
            id: "mgtexp-substantive",
            name: "Management Use of Expert Procedures",
            icon: Calculator,
          },
        ],
      },
      "cmplr-substantive": {
        executionSection: "execution-compliance-with-laws-and-regulations",
        title: "Compliance with Laws and Regulations",
        procedures: [
          {
            id: "cmplr-substantive",
            name: "Compliance Procedures",
            icon: Calculator,
          },
        ],
      },
      "rvestmb-substantive": {
        executionSection: "execution-review-of-estimates-for-management-bias",
        title: "Review of Estimates for Management Bias",
        procedures: [
          {
            id: "rvestmb-substantive",
            name: "Review of Estimates Procedures",
            icon: Calculator,
          },
        ],
      },
      "intadt-substantive": {
        executionSection: "execution-internal-audit",
        title: "Internal Audit",
        procedures: [
          {
            id: "intadt-substantive",
            name: "Internal Audit Procedures",
            icon: Calculator,
          },
        ],
      },
      "fcrp-substantive": {
        executionSection: "execution-fcrp",
        title: "Financial Closing & Reporting Process",
        procedures: [
          { id: "fcrp-substantive", name: "FCRP Procedures", icon: Calculator },
        ],
      },
      "moc-substantive": {
        executionSection: "execution-moc",
        title: "Management Override of Controls",
        procedures: [
          { id: "moc-substantive", name: "MOC Procedures", icon: Calculator },
        ],
      },
      "bapb-substantive": {
        executionSection: "execution-bapb",
        title: "Biological Assets other than Bearer Plants",
        procedures: [
          { id: "bapb-ipe-testing", name: "IPE Testing", icon: Settings },
          { id: "bapb-valuation-testing", name: "Valuation Testing", icon: Calculator },
          { id: "bapb-physical-verification", name: "Physical Verification", icon: Boxes },
          { id: "bapb-disclosure-testing", name: "Disclosure Testing", icon: FileText },
        ],
      },
    };

    // Find which substantive procedure section we're in
    // Load procedures entirely from SubstantiveProcedures.json
    let workspaceName: string | null = null;
    let executionSection: string = "";
    let dynamicWorkspaceInfo: {
      workspaceName: string;
      workspaceId: string;
      area: string;
      landingKey: string;
    } | null = null;

    // Determine the area (Planning, Execution, or Concluding Procedures) from activeSection
    const determineArea = ():
      | "Planning"
      | "Execution"
      | "ConcludingProcedures" => {
      if (activeSection?.startsWith("concluding-")) {
        return "ConcludingProcedures";
      }
      if (
        activeSection?.startsWith("planning-") ||
        activeSection === "planning" ||
        activeSection === "planning-overview"
      ) {
        return "Planning";
      }
      return "Execution";
    };

    // Map workspace names to their concluding landing page routes
    const getConcludingLandingRoute = (workspaceName: string): string => {
      const mapping: Record<string, string> = {
        "Going Concern": "concluding-going-concern",
        "Litigations & Claims": "concluding-litigations-claims",
        "Compliance with Laws and Regulations":
          "concluding-compliance-laws-regulations",
        "Review of Estimates for Management Bias":
          "concluding-review-estimates-bias",
        "Internal Audit": "concluding-internal-audit",
        "Evaluate Audit Results Including Misstatements":
          "concluding-evaluate-audit-results",
      };
      return mapping[workspaceName] || "";
    };

    // Get the correct back route based on area
    const getBackRoute = (): string => {
      const area = determineArea();
      if (area === "Planning") {
        return "planning-overview";
      }
      if (area === "ConcludingProcedures" && workspaceName) {
        const concludingRoute = getConcludingLandingRoute(workspaceName);
        if (concludingRoute) {
          return concludingRoute;
        }
      }
      // Default to execution section or execution
      return executionSection || "execution";
    };

    // Get the back button text based on area
    const getBackButtonText = (): string => {
      if (dynamicWorkspaceInfo) {
        return "Back to Workspace";
      }
      const area = determineArea();
      if (area === "Planning") {
        return "Back to Planning";
      }
      if (area === "ConcludingProcedures") {
        return "Back to Concluding Procedures";
      }
      return "Back to Execution";
    };

    if (
      activeSection &&
      activeSection.startsWith("dynamic-workspace-substantive:")
    ) {
      // Parse dynamic workspace substantive procedure route
      const [, area, prefix, encodedName] = activeSection.split(":");
      workspaceName = decodeURIComponent(encodedName || "");
      const workspaceId = prefix || "";
      const landingKey = `dynamic-workspace:${area}:${prefix}:${encodedName}`;

      dynamicWorkspaceInfo = { workspaceName, workspaceId, area, landingKey };
      executionSection = landingKey;
    } else if (activeSection && activeSection.startsWith("custom-procedure:")) {
      // Custom procedure route - extract workspace name
      const parts = activeSection.split(":");
      if (parts.length >= 2) {
        workspaceName = decodeURIComponent(parts[1]);
        if (workspaceName) {
          executionSection = getExecutionSectionFromWorkspace(workspaceName);
        }
      }
    } else if (activeSection) {
      // First, try to match against substantive route prefixes (e.g., "payroll-substantive")
      // Also handle "concluding-" prefixed routes (e.g., "concluding-litclm-substantive")
      let foundViaPrefix = false;
      let normalizedSection = activeSection;

      // Remove "concluding-" prefix if present for matching
      if (activeSection.startsWith("concluding-")) {
        normalizedSection = activeSection.replace("concluding-", "");
      }

      for (const [prefix] of Object.entries(substantiveProceduresMap)) {
        // Check if normalizedSection exactly matches the prefix (e.g., "litclm-substantive")
        if (normalizedSection === prefix) {
          workspaceName = getWorkspaceFromSubstantivePrefix(prefix);
          if (workspaceName) {
            executionSection = getExecutionSectionFromWorkspace(workspaceName);
            foundViaPrefix = true;
          }
          break;
        }

        // Check if normalizedSection starts with the prefix followed by a dash (e.g., "litclm-substantive-litigations-claims")
        if (normalizedSection.startsWith(prefix + "-")) {
          workspaceName = getWorkspaceFromSubstantivePrefix(prefix);
          if (workspaceName) {
            executionSection = getExecutionSectionFromWorkspace(workspaceName);
            foundViaPrefix = true;
          }
          break;
        }
      }

      // If not found via prefix, try reverse lookup from procedure route
      // This handles routes like "inv-nrv-assessment" that don't have "-substantive" in them
      if (!foundViaPrefix && !workspaceName) {
        workspaceName = getWorkspaceFromProcedureRoute(activeSection);
        if (workspaceName) {
          executionSection = getExecutionSectionFromWorkspace(workspaceName);
        }
      }
    }

    // If we found a workspace, show procedures list (even if data is still loading)
    // This ensures the left panel stays on procedures when a procedure route is active
    if (workspaceName) {
      // Wait for procedures data to load, but show the procedures panel structure
      if (!loadedProceduresData) {
        // Data is still loading - show loading state but keep the procedures panel structure
        return (
          <>
            <div className="border-b border-white/10 bg-white/5 p-4 backdrop-blur-sm">
              <h3 className="mb-1 text-sm font-semibold text-white">
                Substantive Procedures
              </h3>
              <p className="text-xs text-white/60">{workspaceName}</p>
            </div>
            <div className="flex min-h-0 flex-1 flex-col items-center justify-center p-4">
              <div className="text-sm text-white/60">Loading procedures...</div>
            </div>
          </>
        );
      }

      // Helpers for procedure-type color coding (matches GenericSubstantiveProcedures)
      const getCategoryForProcedureId = (
        procedureId: string,
        procedureName?: string,
      ): "testing" | "analysis" | "compliance" | "module" => {
        if (
          procedureName &&
          (procedureName === "Ageing Analysis" ||
            procedureName.startsWith("Ageing Analysis - ") ||
            procedureName === "Composition Analysis" ||
            procedureName.startsWith("Composition Analysis - "))
        ) {
          return "module";
        }
        const id = procedureId.replace(/^concluding-/, "").toLowerCase();
        if (
          id.includes("ipe") ||
          id.includes("exception") ||
          id.includes("testing")
        )
          return "testing";
        if (
          id.includes("analysis") ||
          id.includes("reconciliation") ||
          id.includes("ageing")
        )
          return "analysis";
        if (id.includes("compliance") || id.includes("remuneration"))
          return "compliance";
        return "testing";
      };
      const getProcedureAccent = (
        procedureId: string,
        isActive: boolean,
        procedureName?: string,
      ) => {
        const category = getCategoryForProcedureId(procedureId, procedureName);
        if (isActive) {
          switch (category) {
            case "testing":
              return {
                border: "border-[#4da3ff]/30",
                bg: "bg-[#4da3ff]/15",
                icon: "text-[#4da3ff]",
                shadow: "shadow-lg shadow-[#4da3ff]/10",
              };
            case "analysis":
              return {
                border: "border-emerald-500/30",
                bg: "bg-emerald-500/15",
                icon: "text-emerald-400",
                shadow: "shadow-lg shadow-emerald-500/10",
              };
            case "compliance":
              return {
                border: "border-violet-500/30",
                bg: "bg-violet-500/15",
                icon: "text-violet-400",
                shadow: "shadow-lg shadow-violet-500/10",
              };
            case "module":
              return {
                border: "border-purple-500/30",
                bg: "bg-purple-500/15",
                icon: "text-purple-400",
                shadow: "shadow-lg shadow-purple-500/10",
              };
            default:
              return {
                border: "border-[#4da3ff]/30",
                bg: "bg-[#4da3ff]/15",
                icon: "text-[#4da3ff]",
                shadow: "shadow-lg shadow-[#4da3ff]/10",
              };
          }
        }
        switch (category) {
          case "testing":
            return {
              border: "border-l-2 border-l-blue-500/40",
              bg: "bg-white/5",
              icon: "text-blue-400/90",
              shadow: "",
            };
          case "analysis":
            return {
              border: "border-l-2 border-l-emerald-500/40",
              bg: "bg-white/5",
              icon: "text-emerald-400/90",
              shadow: "",
            };
          case "compliance":
            return {
              border: "border-l-2 border-l-violet-500/40",
              bg: "bg-white/5",
              icon: "text-violet-400/90",
              shadow: "",
            };
          case "module":
            return {
              border: "border-l-2 border-l-purple-500/40",
              bg: "bg-white/5",
              icon: "text-purple-400/90",
              shadow: "",
            };
          default:
            return {
              border: "border-l-2 border-l-blue-500/40",
              bg: "bg-white/5",
              icon: "text-blue-400/90",
              shadow: "",
            };
        }
      };

      // If we have data for this workspace, show procedures list
      const workspaceProceduresForArea =
        workspaceName === "Lease Liability"
          ? loadedProceduresData["Lease Liability"]
          : workspaceName === "Lease Liability - Ind AS"
            ? loadedProceduresData["Lease Liability - Ind AS"] ||
              loadedProceduresData["Lease Liability"]
            : loadedProceduresData[workspaceName];
      if (workspaceProceduresForArea) {
        const workspaceProcedures = workspaceProceduresForArea;
        const standardProcedures =
          getStandardProceduresForWorkspace(workspaceName, engagementFrf);
        const standardProcedureNames = new Set(
          standardProcedures.map((p) => p.name),
        );

        // Build procedures list from SubstantiveProcedures.json
        const proceduresToShow: Array<{
          id: string;
          name: string;
          icon: React.ComponentType<{ className?: string }>;
        }> = [];

        // Determine if we're in Concluding Procedures
        const area = determineArea();
        const isConcluding = area === "ConcludingProcedures";

        for (const [procedureName] of Object.entries(workspaceProcedures)) {
          if (
            workspaceName === "Lease Liability - Ind AS" &&
            procedureName.trim() === "Lease Accounting Exception" &&
            leaseIndASQ4Answer.trim().toLowerCase() !== "yes"
          ) {
            continue;
          }

          if (
            engagementFrf === FRF_IND_AS &&
            workspaceName === "Other Receivables" &&
            (procedureName.trim() === "Advance to Suppliers - Completeness Check" ||
              procedureName.trim() === "Capital Advances - Completeness Check")
          ) {
            continue;
          }
          if (
            engagementFrf === FRF_AS &&
            workspaceName === "Other Assets" &&
            (procedureName.trim() === "Advance to Suppliers - Completeness Check" ||
              procedureName.trim() === "Capital Advances - Completeness Check")
          ) {
            continue;
          }

          // Filter procedures based on area/phase
          // For Litigations & Claims: "Assessment" should only appear in Concluding Procedures
          if (
            workspaceName === "Litigations & Claims" &&
            procedureName.trim() === "Assessment"
          ) {
            if (!isConcluding) {
              // Skip Assessment procedure if not in Concluding Procedures
              continue;
            }
          }

          if (standardProcedureNames.has(procedureName)) {
            // Standard procedure - find it in the standard procedures list
            const standardProc = standardProcedures.find(
              (p) => p.name === procedureName,
            );
            if (standardProc) {
              let routeId = getProcedureRouteId(
                workspaceName,
                procedureName,
                "standard",
              );
              // Add "concluding-" prefix if we're in Concluding Procedures
              if (
                isConcluding &&
                routeId &&
                !routeId.startsWith("concluding-")
              ) {
                routeId = `concluding-${routeId}`;
              }

              // Harden routing for Litigations & Claims Assessment in Concluding area
              if (
                isConcluding &&
                workspaceName === "Litigations & Claims" &&
                procedureName.trim() === "Assessment"
              ) {
                routeId = "concluding-litclm-substantive-assessment";
              }

              proceduresToShow.push({
                id: routeId,
                name: procedureName,
                icon: standardProc.icon,
              });
            }
          } else {
            // Do not surface migrated completeness procedures as "custom" when not applicable to current FRF
            if (
              workspaceName === "Other Assets" &&
              (procedureName.trim() === "Advance to Suppliers - Completeness Check" ||
                procedureName.trim() === "Capital Advances - Completeness Check") &&
              engagementFrf !== FRF_IND_AS
            ) {
              continue;
            }
            if (
              workspaceName === "Other Receivables" &&
              (procedureName.trim() === "Advance to Suppliers - Completeness Check" ||
                procedureName.trim() === "Capital Advances - Completeness Check") &&
              engagementFrf === FRF_IND_AS
            ) {
              continue;
            }
            // Custom or module procedure
            let customRouteId = `custom-procedure:${encodeURIComponent(workspaceName)}:${encodeURIComponent(procedureName)}`;
            // For Concluding Procedures, we might need to handle custom procedures differently
            // but for now, keep the same format
            proceduresToShow.push({
              id: customRouteId,
              name: procedureName,
              icon: FileCheck,
            });
          }
        }

        return (
          <>
            <div className="border-b border-white/10 bg-white/5 p-4 backdrop-blur-sm">
              <h3 className="mb-1 text-sm font-semibold text-white">
                Substantive Procedures
              </h3>
              <p className="text-xs text-white/60">{workspaceName}</p>
            </div>
            <div className="flex min-h-0 flex-1 flex-col p-4">
              {/* Back button */}
              <button
                onClick={() => setActiveSection(getBackRoute())}
                className="mb-3 flex w-full items-center gap-2 rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-white/80 transition-all duration-200 hover:bg-white/10 hover:text-white"
              >
                <ArrowLeft className="h-4 w-4" />
                <span>{getBackButtonText()}</span>
              </button>
              {/* Search Bar */}
              <div className="relative mb-3">
                <Search className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-white/40" />
                <Input
                  type="text"
                  placeholder="Search procedures..."
                  value={substantiveSearchQuery}
                  onChange={(e) => setSubstantiveSearchQuery(e.target.value)}
                  className="w-full border-white/20 bg-white/5 pl-9 text-white placeholder:text-white/40 focus:border-blue-500/50 focus:ring-blue-500/20"
                />
              </div>
              <div className="max-h-[calc(100vh-13rem)] flex-1 space-y-1 overflow-y-auto pr-2">
                {proceduresToShow
                  .filter((procedure) => {
                    const query = substantiveSearchQuery.toLowerCase();
                    return (
                      procedure.name.toLowerCase().includes(query) ||
                      procedure.id.toLowerCase().includes(query)
                    );
                  })
                  .map((procedure) => {
                    const Icon = procedure.icon;
                    // For Concluding Procedures, also check without the "concluding-" prefix for active state
                    // since activeSection might have the prefix
                    const normalizedActiveSection =
                      isConcluding && activeSection?.startsWith("concluding-")
                        ? activeSection.replace("concluding-", "")
                        : activeSection;
                    const normalizedProcedureId =
                      isConcluding && procedure.id?.startsWith("concluding-")
                        ? procedure.id.replace("concluding-", "")
                        : procedure.id;
                    const isActive =
                      activeSection === procedure.id ||
                      normalizedActiveSection === normalizedProcedureId ||
                      (procedure.id.startsWith("custom-procedure:") &&
                        activeSection.startsWith("custom-procedure:") &&
                        activeSection === procedure.id);
                    const isCustomProcedure =
                      procedure.id.startsWith("custom-procedure:");
                    const accent = getProcedureAccent(
                      procedure.id,
                      isActive,
                      procedure.name,
                    );
                    return (
                      <button
                        key={procedure.id}
                        onClick={() => setActiveSection(procedure.id)}
                        className={`group flex w-full items-center gap-3 rounded-lg px-3 py-3 text-sm transition-all duration-200 ${
                          isActive
                            ? `border ${accent.border} ${accent.bg} text-white ${accent.shadow}`
                            : `text-white/70 hover:bg-white/10 hover:text-white ${accent.border} ${accent.bg}`
                        }`}
                      >
                        <Icon
                          className={`h-4 w-4 shrink-0 ${isActive ? accent.icon : "text-white/50 group-hover:text-white"}`}
                        />
                        <div className="min-w-0 flex-1 text-left">
                          <div className="font-medium">{procedure.name}</div>
                          {isCustomProcedure && (
                            <div className="mt-0.5 text-xs text-white/40">
                              Custom
                            </div>
                          )}
                        </div>
                        {isActive && (
                          <ChevronRight className="h-4 w-4 shrink-0" />
                        )}
                      </button>
                    );
                  })}
                {proceduresToShow.filter((procedure) => {
                  const query = substantiveSearchQuery.toLowerCase();
                  return (
                    procedure.name.toLowerCase().includes(query) ||
                    procedure.id.toLowerCase().includes(query)
                  );
                }).length === 0 &&
                  substantiveSearchQuery && (
                    <div className="py-6 text-center text-sm text-white/60">
                      No procedures found matching "{substantiveSearchQuery}"
                    </div>
                  )}
              </div>
            </div>
          </>
        );
      } else {
        // Workspace found but no procedures data yet - show empty state
        return (
          <>
            <div className="border-b border-white/10 bg-white/5 p-4 backdrop-blur-sm">
              <h3 className="mb-1 text-sm font-semibold text-white">
                Substantive Procedures
              </h3>
              <p className="text-xs text-white/60">{workspaceName}</p>
            </div>
            <div className="flex min-h-0 flex-1 flex-col p-4">
              <button
                onClick={() => setActiveSection(getBackRoute())}
                className="mb-3 flex w-full items-center gap-2 rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-white/80 transition-all duration-200 hover:bg-white/10 hover:text-white"
              >
                <ArrowLeft className="h-4 w-4" />
                <span>{getBackButtonText()}</span>
              </button>
              <div className="py-6 text-center text-sm text-white/60">
                No procedures found for this workspace.
              </div>
            </div>
          </>
        );
      }
    }

    // Otherwise, show normal execution phases
    return (
      <>
        <div className="flex items-center justify-between border-b border-white/10 bg-white/5 p-4 backdrop-blur-sm">
          <div>
            <h3 className="mb-1 text-sm font-semibold text-white">Execution</h3>
            <p className="text-xs text-white/60">Run audit procedures</p>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                size="icon"
                variant="outline"
                className="h-7 w-7 border-white/20 text-white/80 hover:bg-white/10 hover:text-white"
                title="Add Workspace"
              >
                <Plus className="h-3 w-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="border-white/10 bg-black/90 text-white"
            >
              <DropdownMenuItem
                onClick={() => openStandardWorkspaceDialog("Execution")}
                className="cursor-pointer hover:bg-white/10"
              >
                Standard Workspace
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => openCustomWorkspaceDialog("Execution")}
                className="cursor-pointer hover:bg-white/10"
              >
                Custom Workspace
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => openDeleteWorkspaceDialog("Execution")}
                className="cursor-pointer hover:bg-white/10"
              >
                Delete Workspace
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <div className="flex min-h-0 flex-1 flex-col p-4">
          {/* Search Bar */}
          <div className="relative mb-3">
            <Search className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-white/40" />
            <Input
              type="text"
              placeholder="Search execution items..."
              value={executionSearchQuery}
              onChange={(e) => setExecutionSearchQuery(e.target.value)}
              className="w-full border-white/20 bg-white/5 pl-9 text-white placeholder:text-white/40 focus:border-blue-500/50 focus:ring-blue-500/20"
            />
          </div>
          <div
            ref={executionListContainerRef}
            className="max-h-[calc(100vh-17rem)] flex-1 space-y-1 overflow-y-auto pr-2"
          >
            {/* Helper function to check if item matches search */}
            {(() => {
              const query = executionSearchQuery.toLowerCase();
              const matchesSearch = (title: string, description: string) =>
                !query ||
                title.toLowerCase().includes(query) ||
                description.toLowerCase().includes(query);

              // Map Execution section titles (from Sections.json) to existing routes/icons/descriptions
              const executionConfigByTitle: Record<
                string,
                {
                  section: string;
                  description: string;
                  icon: React.ComponentType<{ className?: string }>;
                }
              > = {
                "Employee Benefits Expense": {
                  section: "execution-payroll",
                  description: "Process employee benefit expense files",
                  icon: Users,
                },
                "Property, Plant & Equipment": {
                  section: "execution-ppe",
                  description: "Process PPE audit procedures",
                  icon: Building2,
                },
                "Intangible Assets": {
                  section: "execution-ia",
                  description: "Process Intangible Assets audit procedures",
                  icon: Building2,
                },
                Depreciation: {
                  section: "execution-depreciation",
                  description: "Depreciation audit workflows",
                  icon: Calculator,
                },
                "Impairment Testing": {
                  section: "execution-impairment",
                  description: "Impairment testing workflows",
                  icon: AlertTriangle,
                },
                "Trade Receivables": {
                  section: "execution-trade-receivables",
                  description: "Trade Receivables audit workflows",
                  icon: Calculator,
                },
                "Other Expenses": {
                  section: "execution-other-expenses",
                  description: "Other Expenses audit workflows",
                  icon: Calculator,
                },
                Borrowings: {
                  section: "execution-borrowings",
                  description: "Borrowings audit workflows",
                  icon: Calculator,
                },
                "Borrowings - Ind AS": {
                  section: "execution-borrowings-ind-as",
                  description: "Borrowings - Ind AS audit workflows",
                  icon: Calculator,
                },
                "Cost of Materials Consumed": {
                  section: "execution-comc",
                  description: "COMC audit workflows",
                  icon: Calculator,
                },
                "Lease Liability": {
                  section: "execution-lease-liability",
                  description: "Lease liability audit workflows",
                  icon: Building2,
                },
                "Lease Liability - Ind AS": {
                  section: "execution-lease-liability-ind-as",
                  description: "Lease liability - Ind AS audit workflows",
                  icon: Building2,
                },
                "Right-of-Use Assets": {
                  section: "execution-right-of-use-assets",
                  description: "Right-of-Use Assets audit workflows",
                  icon: Building2,
                },
                "Provisions & Contingent Liabilities": {
                  section: "execution-pcl",
                  description: "PCL audit workflows",
                  icon: Calculator,
                },
                "Trade Payables": {
                  section: "execution-trade-payables",
                  description: "Trade Payables audit workflows",
                  icon: Calculator,
                },
                Inventory: {
                  section: "execution-inv",
                  description: "Inventory audit workflows",
                  icon: Calculator,
                },
                "Cash & Cash Equivalents": {
                  section: "execution-cce",
                  description: "CCE audit workflows",
                  icon: Calculator,
                },
                "Finance Cost": {
                  section: "execution-fincst",
                  description: "Finance Cost audit workflows",
                  icon: Calculator,
                },
                "Income Tax": {
                  section: "execution-inctx",
                  description: "Income Tax audit workflows",
                  icon: Calculator,
                },
                Investments: {
                  section: "execution-invsmt",
                  description: "Investments audit workflows",
                  icon: Calculator,
                },
                "Other Assets": {
                  section: "execution-other-assets",
                  description: "Other Assets audit workflows",
                  icon: Calculator,
                },
                "Other Financial Assets": {
                  section: "execution-other-financial-assets",
                  description: "Other Financial Assets audit workflows",
                  icon: Calculator,
                },
                "Other Income": {
                  section: "execution-other-income",
                  description: "Other Income audit workflows",
                  icon: Calculator,
                },
                "Revenue from Operations": {
                  section: "execution-rfo",
                  description: "Revenue from Operations audit workflows",
                  icon: Calculator,
                },
                "Other Liabilities": {
                  section: "execution-other-liabilities",
                  description: "Other Liabilities audit workflows",
                  icon: Calculator,
                },
                "Other Financial Liabilities": {
                  section: "execution-other-financial-liabilities",
                  description: "Other Financial Liabilities audit workflows",
                  icon: Calculator,
                },
                "Other Receivables": {
                  section: "execution-other-receivables",
                  description: "Other Receivables audit workflows",
                  icon: Calculator,
                },
                "Opening Balance Testing": {
                  section: "execution-opening-balance-testing",
                  description: "Opening Balance Testing audit workflows",
                  icon: Calculator,
                },
                "Related Party Disclosure": {
                  section: "execution-related-party-disclosure",
                  description: "Related Party Disclosure audit workflows",
                  icon: Calculator,
                },
                Equity: {
                  section: "execution-equity",
                  description: "Equity audit workflows",
                  icon: Calculator,
                },
                "Management Use of Expert": {
                  section: "execution-management-use-of-expert",
                  description: "Management Use of Expert audit workflows",
                  icon: Calculator,
                },
                "Financial Closing & Reporting Process": {
                  section: "execution-fcrp",
                  description:
                    "Financial Closing & Reporting Process audit workflows",
                  icon: Calculator,
                },
                "Management Override of Controls": {
                  section: "execution-moc",
                  description:
                    "Management Override of Controls audit workflows",
                  icon: Calculator,
                },
                "Biological Assets other than Bearer Plants": {
                  section: "execution-bapb",
                  description:
                    "Biological Assets other than Bearer Plants audit workflows",
                  icon: Calculator,
                },
              };

              // Only show workspaces that are present in Sections.json
              const dynamicExecutionItems =
                sectionsJson?.Execution &&
                typeof sectionsJson.Execution === "object"
                  ? (
                      Object.entries(sectionsJson.Execution) as [
                        string,
                        string,
                      ][]
                    ).map(([title, prefix]) => {
                      const config = executionConfigByTitle[title];
                      if (config) {
                        return { title, ...config };
                      }
                      const sectionId = `dynamic-workspace:Execution:${prefix}:${encodeURIComponent(
                        title,
                      )}`;
                      return {
                        title,
                        section: sectionId,
                        description: `${title} audit workflows`,
                        icon: Calculator,
                      };
                    })
                  : [];

              const executionItems = dynamicExecutionItems || [];

              const filteredItems = executionItems.filter((item) =>
                matchesSearch(item.title, item.description),
              );

              // Show empty state if no workspaces in Sections.json
              if (executionItems.length === 0) {
                return (
                  <div className="py-8 text-center text-sm text-white/60">
                    No execution workspaces found. Add workspaces using the "+"
                    button above.
                  </div>
                );
              }

              return (
                <>
                  {filteredItems.map((item) => {
                    const Icon = item.icon;
                    const isActive = activeSection === item.section;
                    return (
                      <button
                        key={item.section}
                        ref={(el) => {
                          executionItemRefs.current[item.section] = el;
                        }}
                        onClick={() => setActiveSection(item.section)}
                        className={`group flex w-full items-center gap-3 rounded-lg px-3 py-3 text-sm transition-all duration-200 ${
                          isActive
                            ? "border border-[#4da3ff]/30 bg-[#4da3ff]/15 text-white shadow-lg shadow-[#4da3ff]/10"
                            : "text-white/70 hover:bg-white/5 hover:text-white"
                        }`}
                      >
                        <Icon
                          className={`h-4 w-4 ${isActive ? "text-[#4da3ff]" : "text-white/50 group-hover:text-white"}`}
                        />
                        <div className="flex-1 text-left">
                          <div className="font-medium">{item.title}</div>
                          <div
                            className={`text-xs ${isActive ? "text-[#4da3ff]/70" : "text-white/50"}`}
                          >
                            {item.description}
                          </div>
                        </div>
                        {isActive && <ChevronRight className="h-4 w-4" />}
                      </button>
                    );
                  })}
                  {filteredItems.length === 0 && executionSearchQuery && (
                    <div className="py-6 text-center text-sm text-white/60">
                      No items found matching "{executionSearchQuery}"
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        </div>
      </>
    );
  };

  const renderPlanningContent = () => (
    <>
      <div className="flex items-center justify-between border-b border-white/10 bg-white/5 p-4 backdrop-blur-sm">
        <div>
          <h3 className="mb-1 text-sm font-semibold text-white">Planning</h3>
          <p className="text-xs text-white/60">Audit planning procedures</p>
        </div>
        <DropdownMenu open={planningDropdownOpen} onOpenChange={setPlanningDropdownOpen}>
          <DropdownMenuTrigger asChild>
            <Button
              size="icon"
              variant="outline"
              className="h-7 w-7 border-white/20 text-white/80 hover:bg-white/10 hover:text-white"
              title="Add Workspace"
            >
              <Plus className="h-3 w-3" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="border-white/10 bg-black/90 text-white"
          >
            <DropdownMenuItem
              onClick={() => openStandardWorkspaceDialog("Planning")}
              className="cursor-pointer hover:bg-white/10"
            >
              Standard Workspace
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => openCustomWorkspaceDialog("Planning")}
              className="cursor-pointer hover:bg-white/10"
            >
              Custom Workspace
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => openDeleteWorkspaceDialog("Planning")}
              className="cursor-pointer hover:bg-white/10"
            >
              Delete Workspace
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <div className="flex min-h-0 flex-1 flex-col p-4">
        {/* Search Bar */}
        <div className="relative mb-3">
          <Search className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-white/40" />
          <Input
            type="text"
            placeholder="Search planning items..."
            value={planningSearchQuery}
            onChange={(e) => setPlanningSearchQuery(e.target.value)}
            className="w-full border-white/20 bg-white/5 pl-9 text-white placeholder:text-white/40 focus:border-blue-500/50 focus:ring-blue-500/20"
          />
        </div>
        <div className="max-h-[calc(100vh-17rem)] flex-1 space-y-1 overflow-y-auto pr-2">
          {/* Helper function to check if item matches search */}
          {(() => {
            const query = planningSearchQuery.toLowerCase();
            const matchesSearch = (title: string, description: string) =>
              !query ||
              title.toLowerCase().includes(query) ||
              description.toLowerCase().includes(query);

            const planningConfigByTitle: Record<
              string,
              {
                section: string;
                description: string;
                icon: React.ComponentType<{ className?: string }>;
              }
            > = {
              "Engagement Acceptance & Continuance": {
                section: "planning-engagement-acceptance",
                description:
                  "Assess engagement acceptance and continuance requirements",
                icon: FileCheck,
              },
              "Understanding the Entity & its Environment": {
                section: "planning-understanding-entity",
                description: "Analyze entity and its business environment",
                icon: Building,
              },
              Materiality: {
                section: "planning-materiality",
                description: "Determine materiality thresholds and benchmarks",
                icon: Calculator,
              },
              "Preliminary Analytical Procedures": {
                section: "planning-preliminary-analytical",
                description: "Perform preliminary analytical procedures",
                icon: BarChart3,
              },
              "Fraud Risk Assessment": {
                section: "planning-fraud-risk",
                description: "Assess fraud risks and responses",
                icon: Shield,
              },
              "IT Risk Assessment": {
                section: "planning-it-risk",
                description: "IT risk assessment",
                icon: Monitor,
              },
              Independence: {
                section: "planning-independence",
                description:
                  "Independence, competence and capability of the engagement team",
                icon: Shield,
              },
            };

            // Only show workspaces that are present in Sections.json
            const dynamicPlanningItems =
              sectionsJson?.Planning &&
              typeof sectionsJson.Planning === "object"
                ? (
                    Object.entries(sectionsJson.Planning) as [string, string][]
                  ).map(([title, prefix]) => {
                    const config = planningConfigByTitle[title];
                    if (config) {
                      return { title, ...config };
                    }
                    const sectionId = `dynamic-workspace:Planning:${prefix}:${encodeURIComponent(
                      title,
                    )}`;
                    return {
                      title,
                      section: sectionId,
                      description: `${title} planning workspace`,
                      icon: FileCheck,
                    };
                  })
                : [];

            const planningItems = dynamicPlanningItems || [];

            const filteredItems = planningItems.filter((item) =>
              matchesSearch(item.title, item.description),
            );

            // Show empty state if no workspaces in Sections.json
            if (planningItems.length === 0) {
              return (
                <div className="py-8 text-center text-sm text-white/60">
                  No planning workspaces found. Add workspaces using the "+"
                  button above.
                </div>
              );
            }

            return (
              <>
                {filteredItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = activeSection === item.section;
                  return (
                    <button
                      key={item.section}
                      onClick={() => setActiveSection(item.section)}
                      className={`group flex w-full items-center gap-3 rounded-lg px-3 py-3 text-sm transition-all duration-200 ${
                        isActive
                          ? "border border-[#4da3ff]/30 bg-[#4da3ff]/15 text-white shadow-lg shadow-[#4da3ff]/10"
                          : "text-white/70 hover:bg-white/5 hover:text-white"
                      }`}
                    >
                      <Icon
                        className={`h-4 w-4 ${isActive ? "text-[#4da3ff]" : "text-white/50 group-hover:text-white"}`}
                      />
                      <div className="flex-1 text-left">
                        <div className="font-medium">{item.title}</div>
                        <div
                          className={`text-xs ${isActive ? "text-[#4da3ff]/70" : "text-white/50"}`}
                        >
                          {item.description}
                        </div>
                      </div>
                      {isActive && <ChevronRight className="h-4 w-4" />}
                    </button>
                  );
                })}
                {filteredItems.length === 0 && planningSearchQuery && (
                  <div className="py-6 text-center text-sm text-white/60">
                    No items found matching "{planningSearchQuery}"
                  </div>
                )}
              </>
            );
          })()}
        </div>
      </div>
    </>
  );

  const renderConcludingContent = () => (
    <>
      <div className="border-b border-white/10 bg-white/5 p-4 backdrop-blur-sm">
        <h3 className="mb-1 text-sm font-semibold text-white">
          Concluding Procedures
        </h3>
        <p className="text-xs text-white/60">Finalize and conclude the audit</p>
      </div>
      <div className="flex min-h-0 flex-1 flex-col p-4">
        {/* Search Bar */}
        <div className="relative mb-3">
          <Search className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-white/40" />
          <Input
            type="text"
            placeholder="Search concluding items..."
            value={concludingSearchQuery}
            onChange={(e) => setConcludingSearchQuery(e.target.value)}
            className="w-full border-white/20 bg-white/5 pl-9 text-white placeholder:text-white/40 focus:border-blue-500/50 focus:ring-blue-500/20"
          />
        </div>
        <div className="max-h-[calc(100vh-260px)] flex-1 space-y-1 overflow-y-auto pr-2">
          {/* 1. CARO */}
          <button
            onClick={() => setActiveSection("concluding-caro")}
            className={`group flex w-full items-center gap-3 rounded-lg px-3 py-3 text-sm transition-all duration-200 ${
              activeSection === "concluding-caro"
                ? "border border-[#4da3ff]/30 bg-[#4da3ff]/15 text-white shadow-lg shadow-[#4da3ff]/10"
                : "text-white/70 hover:bg-white/5 hover:text-white"
            }`}
          >
            <CheckCircle
              className={`h-4 w-4 ${
                activeSection === "concluding-caro"
                  ? "text-[#4da3ff]"
                  : "text-white/50 group-hover:text-white"
              }`}
            />
            <div className="flex-1 text-left">
              <div className="font-medium">CARO</div>
              <div
                className={`text-xs ${
                  activeSection === "concluding-caro"
                    ? "text-[#4da3ff]/70"
                    : "text-white/50"
                }`}
              >
                Companies Auditor's Report Order
              </div>
            </div>
            {activeSection === "concluding-caro" && (
              <ChevronRight className="h-4 w-4" />
            )}
          </button>

          {/* 2. Audit Report & Opinion */}
          <button
            onClick={() => setActiveSection("concluding-audit-report-opinion")}
            className={`group flex w-full items-center gap-3 rounded-lg px-3 py-3 text-sm transition-all duration-200 ${
              activeSection === "concluding-audit-report-opinion"
                ? "border border-[#4da3ff]/30 bg-[#4da3ff]/15 text-white shadow-lg shadow-[#4da3ff]/10"
                : "text-white/70 hover:bg-white/5 hover:text-white"
            }`}
          >
            <FileText
              className={`h-4 w-4 ${
                activeSection === "concluding-audit-report-opinion"
                  ? "text-[#4da3ff]"
                  : "text-white/50 group-hover:text-white"
              }`}
            />
            <div className="flex-1 text-left">
              <div className="font-medium">Audit Report & Opinion</div>
            </div>
            {activeSection === "concluding-audit-report-opinion" && (
              <ChevronRight className="h-4 w-4" />
            )}
          </button>

          {/* 3. Communication with those charged with governance */}
          <button
            onClick={() =>
              setActiveSection("concluding-communication-governance")
            }
            className={`group flex w-full items-center gap-3 rounded-lg px-3 py-3 text-sm transition-all duration-200 ${
              activeSection === "concluding-communication-governance"
                ? "border border-[#4da3ff]/30 bg-[#4da3ff]/15 text-white shadow-lg shadow-[#4da3ff]/10"
                : "text-white/70 hover:bg-white/5 hover:text-white"
            }`}
          >
            <Mail
              className={`h-4 w-4 ${
                activeSection === "concluding-communication-governance"
                  ? "text-[#4da3ff]"
                  : "text-white/50 group-hover:text-white"
              }`}
            />
            <div className="flex-1 text-left">
              <div className="font-medium">
                Communication with those charged with governance
              </div>
            </div>
            {activeSection === "concluding-communication-governance" && (
              <ChevronRight className="h-4 w-4" />
            )}
          </button>

          {/* 4. Subsequent Events */}
          <button
            onClick={() => setActiveSection("concluding-subsequent-events")}
            className={`group flex w-full items-center gap-3 rounded-lg px-3 py-3 text-sm transition-all duration-200 ${
              activeSection === "concluding-subsequent-events"
                ? "border border-[#4da3ff]/30 bg-[#4da3ff]/15 text-white shadow-lg shadow-[#4da3ff]/10"
                : "text-white/70 hover:bg-white/5 hover:text-white"
            }`}
          >
            <Activity
              className={`h-4 w-4 ${
                activeSection === "concluding-subsequent-events"
                  ? "text-[#4da3ff]"
                  : "text-white/50 group-hover:text-white"
              }`}
            />
            <div className="flex-1 text-left">
              <div className="font-medium">Subsequent Events</div>
            </div>
            {activeSection === "concluding-subsequent-events" && (
              <ChevronRight className="h-4 w-4" />
            )}
          </button>

          {/* 4a. Obtain Written Representations */}
          <button
            onClick={() =>
              setActiveSection("concluding-written-representations")
            }
            className={`group flex w-full items-center gap-3 rounded-lg px-3 py-3 text-sm transition-all duration-200 ${
              activeSection === "concluding-written-representations"
                ? "border border-[#4da3ff]/30 bg-[#4da3ff]/15 text-white shadow-lg shadow-[#4da3ff]/10"
                : "text-white/70 hover:bg-white/5 hover:text-white"
            }`}
          >
            <Activity
              className={`h-4 w-4 ${
                activeSection === "concluding-written-representations"
                  ? "text-[#4da3ff]"
                  : "text-white/50 group-hover:text-white"
              }`}
            />
            <div className="flex-1 text-left">
              <div className="font-medium">Obtain Written Representations</div>
            </div>
            {activeSection === "concluding-written-representations" && (
              <ChevronRight className="h-4 w-4" />
            )}
          </button>

          {/* 5. Litigations & Claims */}
          <button
            onClick={() => setActiveSection("concluding-litigations-claims")}
            className={`group flex w-full items-center gap-3 rounded-lg px-3 py-3 text-sm transition-all duration-200 ${
              activeSection === "concluding-litigations-claims"
                ? "border border-[#4da3ff]/30 bg-[#4da3ff]/15 text-white shadow-lg shadow-[#4da3ff]/10"
                : "text-white/70 hover:bg-white/5 hover:text-white"
            }`}
          >
            <Gavel
              className={`h-4 w-4 ${
                activeSection === "concluding-litigations-claims"
                  ? "text-[#4da3ff]"
                  : "text-white/50 group-hover:text-white"
              }`}
            />
            <div className="flex-1 text-left">
              <div className="font-medium">Litigations & Claims</div>
            </div>
            {activeSection === "concluding-litigations-claims" && (
              <ChevronRight className="h-4 w-4" />
            )}
          </button>

          {/* 6. Going Concern */}
          <button
            onClick={() => setActiveSection("execution-borrowings")}
            className={`group flex w-full items-center gap-3 rounded-lg px-3 py-3 text-sm transition-all duration-200 ${
              activeSection === "execution-borrowings"
                ? "border border-[#4da3ff]/30 bg-[#4da3ff]/15 text-white shadow-lg shadow-[#4da3ff]/10"
                : "text-white/70 hover:bg-white/5 hover:text-white"
            }`}
          >
            <Calculator
              className={`h-4 w-4 ${activeSection === "execution-borrowings" ? "text-[#4da3ff]" : "text-white/50 group-hover:text-white"}`}
            />
            <div className="flex-1 text-left">
              <div className="font-medium">Borrowings</div>
              <div
                className={`text-xs ${activeSection === "execution-borrowings" ? "text-[#4da3ff]/70" : "text-white/50"}`}
              >
                Borrowings audit workflows
              </div>
            </div>
            {activeSection === "execution-borrowings" && (
              <ChevronRight className="h-4 w-4" />
            )}
          </button>

          {/* 9. Cost of Materials Consumed */}
          <button
            onClick={() => setActiveSection("execution-comc")}
            className={`group flex w-full items-center gap-3 rounded-lg px-3 py-3 text-sm transition-all duration-200 ${
              activeSection === "execution-comc"
                ? "border border-[#4da3ff]/30 bg-[#4da3ff]/15 text-white shadow-lg shadow-[#4da3ff]/10"
                : "text-white/70 hover:bg-white/5 hover:text-white"
            }`}
          >
            <Calculator
              className={`h-4 w-4 ${activeSection === "execution-comc" ? "text-[#4da3ff]" : "text-white/50 group-hover:text-white"}`}
            />
            <div className="flex-1 text-left">
              <div className="font-medium">Cost of Materials Consumed</div>
              <div
                className={`text-xs ${activeSection === "execution-comc" ? "text-[#4da3ff]/70" : "text-white/50"}`}
              >
                COMC audit workflows
              </div>
            </div>
            {activeSection === "execution-comc" && (
              <ChevronRight className="h-4 w-4" />
            )}
          </button>

          {/* 10. Lease Liability */}
          <button
            onClick={() => setActiveSection("execution-lease-liability")}
            className={`group flex w-full items-center gap-3 rounded-lg px-3 py-3 text-sm transition-all duration-200 ${
              activeSection === "execution-lease-liability"
                ? "border border-[#4da3ff]/30 bg-[#4da3ff]/15 text-white shadow-lg shadow-[#4da3ff]/10"
                : "text-white/70 hover:bg-white/5 hover:text-white"
            }`}
          >
            <Building2
              className={`h-4 w-4 ${activeSection === "execution-lease-liability" ? "text-[#4da3ff]" : "text-white/50 group-hover:text-white"}`}
            />
            <div className="flex-1 text-left">
              <div className="font-medium">Lease Liability</div>
              <div
                className={`text-xs ${activeSection === "execution-lease-liability" ? "text-[#4da3ff]/70" : "text-white/50"}`}
              >
                Lease liability audit workflows
              </div>
            </div>
            {activeSection === "execution-lease-liability" && (
              <ChevronRight className="h-4 w-4" />
            )}
          </button>

          {/* 10b. Lease Liability - Ind AS */}
          <button
            onClick={() => setActiveSection("execution-lease-liability-ind-as")}
            className={`group flex w-full items-center gap-3 rounded-lg px-3 py-3 text-sm transition-all duration-200 ${
              activeSection === "execution-lease-liability-ind-as"
                ? "border border-[#4da3ff]/30 bg-[#4da3ff]/15 text-white shadow-lg shadow-[#4da3ff]/10"
                : "text-white/70 hover:bg-white/5 hover:text-white"
            }`}
          >
            <Building2
              className={`h-4 w-4 ${activeSection === "execution-lease-liability-ind-as" ? "text-[#4da3ff]" : "text-white/50 group-hover:text-white"}`}
            />
            <div className="flex-1 text-left">
              <div className="font-medium">Lease Liability - Ind AS</div>
              <div
                className={`text-xs ${activeSection === "execution-lease-liability-ind-as" ? "text-[#4da3ff]/70" : "text-white/50"}`}
              >
                Lease liability - Ind AS audit workflows
              </div>
            </div>
            {activeSection === "execution-lease-liability-ind-as" && (
              <ChevronRight className="h-4 w-4" />
            )}
          </button>

          {/* 11. Provisions & Contingent Liabilities */}
          <button
            onClick={() => setActiveSection("execution-pcl")}
            className={`group flex w-full items-center gap-3 rounded-lg px-3 py-3 text-sm transition-all duration-200 ${
              activeSection === "execution-pcl"
                ? "border border-[#4da3ff]/30 bg-[#4da3ff]/15 text-white shadow-lg shadow-[#4da3ff]/10"
                : "text-white/70 hover:bg-white/5 hover:text-white"
            }`}
          >
            <Calculator
              className={`h-4 w-4 ${activeSection === "execution-pcl" ? "text-[#4da3ff]" : "text-white/50 group-hover:text-white"}`}
            />
            <div className="flex-1 text-left">
              <div className="font-medium">
                Provisions & Contingent Liabilities
              </div>
              <div
                className={`text-xs ${activeSection === "execution-pcl" ? "text-[#4da3ff]/70" : "text-white/50"}`}
              >
                PCL audit workflows
              </div>
            </div>
            {activeSection === "execution-pcl" && (
              <ChevronRight className="h-4 w-4" />
            )}
          </button>

          {/* 12. Trade Payables */}
          <button
            onClick={() => setActiveSection("execution-trade-payables")}
            className={`group flex w-full items-center gap-3 rounded-lg px-3 py-3 text-sm transition-all duration-200 ${
              activeSection === "execution-trade-payables"
                ? "border border-[#4da3ff]/30 bg-[#4da3ff]/15 text-white shadow-lg shadow-[#4da3ff]/10"
                : "text-white/70 hover:bg-white/5 hover:text-white"
            }`}
          >
            <Calculator
              className={`h-4 w-4 ${activeSection === "execution-trade-payables" ? "text-[#4da3ff]" : "text-white/50 group-hover:text-white"}`}
            />
            <div className="flex-1 text-left">
              <div className="font-medium">Trade Payables</div>
              <div
                className={`text-xs ${activeSection === "execution-trade-payables" ? "text-[#4da3ff]/70" : "text-white/50"}`}
              >
                Trade Payables audit workflows
              </div>
            </div>
            {activeSection === "execution-trade-payables" && (
              <ChevronRight className="h-4 w-4" />
            )}
          </button>

          {/* 13. Inventory */}
          <button
            onClick={() => setActiveSection("execution-inv")}
            className={`group flex w-full items-center gap-3 rounded-lg px-3 py-3 text-sm transition-all duration-200 ${
              activeSection === "execution-inv"
                ? "border border-[#4da3ff]/30 bg-[#4da3ff]/15 text-white shadow-lg shadow-[#4da3ff]/10"
                : "text-white/70 hover:bg-white/5 hover:text-white"
            }`}
          >
            <Calculator
              className={`h-4 w-4 ${activeSection === "execution-inv" ? "text-[#4da3ff]" : "text-white/50 group-hover:text-white"}`}
            />
            <div className="flex-1 text-left">
              <div className="font-medium">Inventory</div>
              <div
                className={`text-xs ${activeSection === "execution-inv" ? "text-[#4da3ff]/70" : "text-white/50"}`}
              >
                Inventory audit workflows
              </div>
            </div>
            {activeSection === "execution-inv" && (
              <ChevronRight className="h-4 w-4" />
            )}
          </button>

          {/* 14. Cash & Cash Equivalents */}
          <button
            onClick={() => setActiveSection("execution-cce")}
            className={`group flex w-full items-center gap-3 rounded-lg px-3 py-3 text-sm transition-all duration-200 ${
              activeSection === "execution-cce"
                ? "border border-[#4da3ff]/30 bg-[#4da3ff]/15 text-white shadow-lg shadow-[#4da3ff]/10"
                : "text-white/70 hover:bg-white/5 hover:text-white"
            }`}
          >
            <Calculator
              className={`h-4 w-4 ${activeSection === "execution-cce" ? "text-[#4da3ff]" : "text-white/50 group-hover:text-white"}`}
            />
            <div className="flex-1 text-left">
              <div className="font-medium">Cash & Cash Equivalents</div>
              <div
                className={`text-xs ${activeSection === "execution-cce" ? "text-[#4da3ff]/70" : "text-white/50"}`}
              >
                CCE audit workflows
              </div>
            </div>
            {activeSection === "execution-cce" && (
              <ChevronRight className="h-4 w-4" />
            )}
          </button>

          {/* 15. Finance Cost */}
          <button
            onClick={() => setActiveSection("execution-fincst")}
            className={`group flex w-full items-center gap-3 rounded-lg px-3 py-3 text-sm transition-all duration-200 ${
              activeSection === "execution-fincst"
                ? "border border-[#4da3ff]/30 bg-[#4da3ff]/15 text-white shadow-lg shadow-[#4da3ff]/10"
                : "text-white/70 hover:bg-white/5 hover:text-white"
            }`}
          >
            <Calculator
              className={`h-4 w-4 ${activeSection === "execution-fincst" ? "text-[#4da3ff]" : "text-white/50 group-hover:text-white"}`}
            />
            <div className="flex-1 text-left">
              <div className="font-medium">Finance Cost</div>
              <div
                className={`text-xs ${activeSection === "execution-fincst" ? "text-[#4da3ff]/70" : "text-white/50"}`}
              >
                Finance Cost audit workflows
              </div>
            </div>
            {activeSection === "execution-fincst" && (
              <ChevronRight className="h-4 w-4" />
            )}
          </button>

          {/* 16. Income Tax */}
          <button
            onClick={() => setActiveSection("execution-inctx")}
            className={`group flex w-full items-center gap-3 rounded-lg px-3 py-3 text-sm transition-all duration-200 ${
              activeSection === "execution-inctx"
                ? "border border-[#4da3ff]/30 bg-[#4da3ff]/15 text-white shadow-lg shadow-[#4da3ff]/10"
                : "text-white/70 hover:bg-white/5 hover:text-white"
            }`}
          >
            <Calculator
              className={`h-4 w-4 ${activeSection === "execution-inctx" ? "text-[#4da3ff]" : "text-white/50 group-hover:text-white"}`}
            />
            <div className="flex-1 text-left">
              <div className="font-medium">Income Tax</div>
              <div
                className={`text-xs ${activeSection === "execution-inctx" ? "text-[#4da3ff]/70" : "text-white/50"}`}
              >
                Income Tax audit workflows
              </div>
            </div>
            {activeSection === "execution-inctx" && (
              <ChevronRight className="h-4 w-4" />
            )}
          </button>

          {/* 17. Investments */}
          <button
            onClick={() => setActiveSection("execution-invsmt")}
            className={`group flex w-full items-center gap-3 rounded-lg px-3 py-3 text-sm transition-all duration-200 ${
              activeSection === "execution-invsmt"
                ? "border border-[#4da3ff]/30 bg-[#4da3ff]/15 text-white shadow-lg shadow-[#4da3ff]/10"
                : "text-white/70 hover:bg-white/5 hover:text-white"
            }`}
          >
            <Calculator
              className={`h-4 w-4 ${activeSection === "execution-invsmt" ? "text-[#4da3ff]" : "text-white/50 group-hover:text-white"}`}
            />
            <div className="flex-1 text-left">
              <div className="font-medium">Investments</div>
              <div
                className={`text-xs ${activeSection === "execution-invsmt" ? "text-[#4da3ff]/70" : "text-white/50"}`}
              >
                Investments audit workflows
              </div>
            </div>
            {activeSection === "execution-invsmt" && (
              <ChevronRight className="h-4 w-4" />
            )}
          </button>

          {/* 18. Other Assets */}
          <button
            onClick={() => setActiveSection("execution-other-assets")}
            className={`group flex w-full items-center gap-3 rounded-lg px-3 py-3 text-sm transition-all duration-200 ${
              activeSection === "execution-other-assets"
                ? "border border-[#4da3ff]/30 bg-[#4da3ff]/15 text-white shadow-lg shadow-[#4da3ff]/10"
                : "text-white/70 hover:bg-white/5 hover:text-white"
            }`}
          >
            <Calculator
              className={`h-4 w-4 ${activeSection === "execution-other-assets" ? "text-[#4da3ff]" : "text-white/50 group-hover:text-white"}`}
            />
            <div className="flex-1 text-left">
              <div className="font-medium">Other Assets</div>
              <div
                className={`text-xs ${activeSection === "execution-other-assets" ? "text-[#4da3ff]/70" : "text-white/50"}`}
              >
                Other Assets audit workflows
              </div>
            </div>
            {activeSection === "execution-other-assets" && (
              <ChevronRight className="h-4 w-4" />
            )}
          </button>

          {/* 19. Other Financial Assets */}
          <button
            onClick={() => setActiveSection("execution-other-financial-assets")}
            className={`group flex w-full items-center gap-3 rounded-lg px-3 py-3 text-sm transition-all duration-200 ${
              activeSection === "execution-other-financial-assets"
                ? "border border-[#4da3ff]/30 bg-[#4da3ff]/15 text-white shadow-lg shadow-[#4da3ff]/10"
                : "text-white/70 hover:bg-white/5 hover:text-white"
            }`}
          >
            <Calculator
              className={`h-4 w-4 ${activeSection === "execution-other-financial-assets" ? "text-[#4da3ff]" : "text-white/50 group-hover:text-white"}`}
            />
            <div className="flex-1 text-left">
              <div className="font-medium">Other Financial Assets</div>
              <div
                className={`text-xs ${activeSection === "execution-other-financial-assets" ? "text-[#4da3ff]/70" : "text-white/50"}`}
              >
                Other Financial Assets audit workflows
              </div>
            </div>
            {activeSection === "execution-other-financial-assets" && (
              <ChevronRight className="h-4 w-4" />
            )}
          </button>

          {/* 20. Other Income */}
          <button
            onClick={() => setActiveSection("execution-other-income")}
            className={`group flex w-full items-center gap-3 rounded-lg px-3 py-3 text-sm transition-all duration-200 ${
              activeSection === "execution-other-income"
                ? "border border-[#4da3ff]/30 bg-[#4da3ff]/15 text-white shadow-lg shadow-[#4da3ff]/10"
                : "text-white/70 hover:bg-white/5 hover:text-white"
            }`}
          >
            <Calculator
              className={`h-4 w-4 ${activeSection === "execution-other-income" ? "text-[#4da3ff]" : "text-white/50 group-hover:text-white"}`}
            />
            <div className="flex-1 text-left">
              <div className="font-medium">Other Income</div>
              <div
                className={`text-xs ${activeSection === "execution-other-income" ? "text-[#4da3ff]/70" : "text-white/50"}`}
              >
                Other Income audit workflows
              </div>
            </div>
            {activeSection === "execution-other-income" && (
              <ChevronRight className="h-4 w-4" />
            )}
          </button>

          {/* 21. Other Liabilities */}
          <button
            onClick={() => setActiveSection("execution-other-liabilities")}
            className={`group flex w-full items-center gap-3 rounded-lg px-3 py-3 text-sm transition-all duration-200 ${
              activeSection === "execution-other-liabilities"
                ? "border border-[#4da3ff]/30 bg-[#4da3ff]/15 text-white shadow-lg shadow-[#4da3ff]/10"
                : "text-white/70 hover:bg-white/5 hover:text-white"
            }`}
          >
            <Calculator
              className={`h-4 w-4 ${activeSection === "execution-other-liabilities" ? "text-[#4da3ff]" : "text-white/50 group-hover:text-white"}`}
            />
            <div className="flex-1 text-left">
              <div className="font-medium">Other Liabilities</div>
              <div
                className={`text-xs ${activeSection === "execution-other-liabilities" ? "text-[#4da3ff]/70" : "text-white/50"}`}
              >
                Other Liabilities audit workflows
              </div>
            </div>
            {activeSection === "execution-other-liabilities" && (
              <ChevronRight className="h-4 w-4" />
            )}
          </button>

          {/* 21b. Other Financial Liabilities */}
          <button
            onClick={() => setActiveSection("execution-other-financial-liabilities")}
            className={`group flex w-full items-center gap-3 rounded-lg px-3 py-3 text-sm transition-all duration-200 ${
              activeSection === "execution-other-financial-liabilities"
                ? "border border-[#4da3ff]/30 bg-[#4da3ff]/15 text-white shadow-lg shadow-[#4da3ff]/10"
                : "text-white/70 hover:bg-white/5 hover:text-white"
            }`}
          >
            <Calculator
              className={`h-4 w-4 ${activeSection === "execution-other-financial-liabilities" ? "text-[#4da3ff]" : "text-white/50 group-hover:text-white"}`}
            />
            <div className="flex-1 text-left">
              <div className="font-medium">Other Financial Liabilities</div>
              <div
                className={`text-xs ${activeSection === "execution-other-financial-liabilities" ? "text-[#4da3ff]/70" : "text-white/50"}`}
              >
                Other Financial Liabilities audit workflows
              </div>
            </div>
            {activeSection === "execution-other-financial-liabilities" && (
              <ChevronRight className="h-4 w-4" />
            )}
          </button>

          {/* 22. Other Receivables */}
          <button
            onClick={() => setActiveSection("execution-other-receivables")}
            className={`group flex w-full items-center gap-3 rounded-lg px-3 py-3 text-sm transition-all duration-200 ${
              activeSection === "execution-other-receivables"
                ? "border border-[#4da3ff]/30 bg-[#4da3ff]/15 text-white shadow-lg shadow-[#4da3ff]/10"
                : "text-white/70 hover:bg-white/5 hover:text-white"
            }`}
          >
            <Calculator
              className={`h-4 w-4 ${activeSection === "execution-other-receivables" ? "text-[#4da3ff]" : "text-white/50 group-hover:text-white"}`}
            />
            <div className="flex-1 text-left">
              <div className="font-medium">Other Receivables</div>
              <div
                className={`text-xs ${activeSection === "execution-other-receivables" ? "text-[#4da3ff]/70" : "text-white/50"}`}
              >
                Other Receivables audit workflows
              </div>
            </div>
            {activeSection === "execution-other-receivables" && (
              <ChevronRight className="h-4 w-4" />
            )}
          </button>

          {/* 22. Going Concern */}
          <button
            onClick={() => setActiveSection("execution-going-concern")}
            className={`group flex w-full items-center gap-3 rounded-lg px-3 py-3 text-sm transition-all duration-200 ${
              activeSection === "execution-going-concern"
                ? "border border-[#4da3ff]/30 bg-[#4da3ff]/15 text-white shadow-lg shadow-[#4da3ff]/10"
                : "text-white/70 hover:bg-white/5 hover:text-white"
            }`}
          >
            <Calculator
              className={`h-4 w-4 ${activeSection === "execution-going-concern" ? "text-[#4da3ff]" : "text-white/50 group-hover:text-white"}`}
            />
            <div className="flex-1 text-left">
              <div className="font-medium">Going Concern</div>
              <div
                className={`text-xs ${activeSection === "execution-going-concern" ? "text-[#4da3ff]/70" : "text-white/50"}`}
              >
                Going Concern audit workflows
              </div>
            </div>
            {activeSection === "execution-going-concern" && (
              <ChevronRight className="h-4 w-4" />
            )}
          </button>

          {/* 23. Litigations & Claims */}
          <button
            onClick={() => setActiveSection("execution-litigations-claims")}
            className={`group flex w-full items-center gap-3 rounded-lg px-3 py-3 text-sm transition-all duration-200 ${
              activeSection === "execution-litigations-claims"
                ? "border border-[#4da3ff]/30 bg-[#4da3ff]/15 text-white shadow-lg shadow-[#4da3ff]/10"
                : "text-white/70 hover:bg-white/5 hover:text-white"
            }`}
          >
            <Calculator
              className={`h-4 w-4 ${activeSection === "execution-litigations-claims" ? "text-[#4da3ff]" : "text-white/50 group-hover:text-white"}`}
            />
            <div className="flex-1 text-left">
              <div className="font-medium">Litigations & Claims</div>
              <div
                className={`text-xs ${activeSection === "execution-litigations-claims" ? "text-[#4da3ff]/70" : "text-white/50"}`}
              >
                Litigations & Claims audit workflows
              </div>
            </div>
            {activeSection === "execution-litigations-claims" && (
              <ChevronRight className="h-4 w-4" />
            )}
          </button>

          {/* 24. Opening Balance Testing */}
          <button
            onClick={() =>
              setActiveSection("execution-opening-balance-testing")
            }
            className={`group flex w-full items-center gap-3 rounded-lg px-3 py-3 text-sm transition-all duration-200 ${
              activeSection === "execution-opening-balance-testing"
                ? "border border-[#4da3ff]/30 bg-[#4da3ff]/15 text-white shadow-lg shadow-[#4da3ff]/10"
                : "text-white/70 hover:bg-white/5 hover:text-white"
            }`}
          >
            <Calculator
              className={`h-4 w-4 ${activeSection === "execution-opening-balance-testing" ? "text-[#4da3ff]" : "text-white/50 group-hover:text-white"}`}
            />
            <div className="flex-1 text-left">
              <div className="font-medium">Opening Balance Testing</div>
              <div
                className={`text-xs ${activeSection === "execution-opening-balance-testing" ? "text-[#4da3ff]/70" : "text-white/50"}`}
              >
                Opening Balance Testing audit workflows
              </div>
            </div>
            {activeSection === "execution-opening-balance-testing" && (
              <ChevronRight className="h-4 w-4" />
            )}
          </button>

          {/* 25. Related Party Disclosure */}
          <button
            onClick={() =>
              setActiveSection("execution-related-party-disclosure")
            }
            className={`group flex w-full items-center gap-3 rounded-lg px-3 py-3 text-sm transition-all duration-200 ${
              activeSection === "execution-related-party-disclosure"
                ? "border border-[#4da3ff]/30 bg-[#4da3ff]/15 text-white shadow-lg shadow-[#4da3ff]/10"
                : "text-white/70 hover:bg-white/5 hover:text-white"
            }`}
          >
            <Calculator
              className={`h-4 w-4 ${activeSection === "execution-related-party-disclosure" ? "text-[#4da3ff]" : "text-white/50 group-hover:text-white"}`}
            />
            <div className="flex-1 text-left">
              <div className="font-medium">Related Party Disclosure</div>
              <div
                className={`text-xs ${activeSection === "execution-related-party-disclosure" ? "text-[#4da3ff]/70" : "text-white/50"}`}
              >
                Related Party Disclosure audit workflows
              </div>
            </div>
            {activeSection === "execution-related-party-disclosure" && (
              <ChevronRight className="h-4 w-4" />
            )}
          </button>

          {/* 26. Equity */}
          <button
            onClick={() => setActiveSection("execution-equity")}
            className={`group flex w-full items-center gap-3 rounded-lg px-3 py-3 text-sm transition-all duration-200 ${
              activeSection === "execution-equity"
                ? "border border-[#4da3ff]/30 bg-[#4da3ff]/15 text-white shadow-lg shadow-[#4da3ff]/10"
                : "text-white/70 hover:bg-white/5 hover:text-white"
            }`}
          >
            <Calculator
              className={`h-4 w-4 ${activeSection === "execution-equity" ? "text-[#4da3ff]" : "text-white/50 group-hover:text-white"}`}
            />
            <div className="flex-1 text-left">
              <div className="font-medium">Equity</div>
              <div
                className={`text-xs ${activeSection === "execution-equity" ? "text-[#4da3ff]/70" : "text-white/50"}`}
              >
                Equity audit workflows
              </div>
            </div>
            {activeSection === "execution-equity" && (
              <ChevronRight className="h-4 w-4" />
            )}
          </button>

          {/* 27. Management Use of Expert */}
          <button
            onClick={() =>
              setActiveSection("execution-management-use-of-expert")
            }
            className={`group flex w-full items-center gap-3 rounded-lg px-3 py-3 text-sm transition-all duration-200 ${
              activeSection === "execution-management-use-of-expert"
                ? "border border-[#4da3ff]/30 bg-[#4da3ff]/15 text-white shadow-lg shadow-[#4da3ff]/10"
                : "text-white/70 hover:bg-white/5 hover:text-white"
            }`}
          >
            <Calculator
              className={`h-4 w-4 ${activeSection === "execution-management-use-of-expert" ? "text-[#4da3ff]" : "text-white/50 group-hover:text-white"}`}
            />
            <div className="flex-1 text-left">
              <div className="font-medium">Management Use of Expert</div>
              <div
                className={`text-xs ${activeSection === "execution-management-use-of-expert" ? "text-[#4da3ff]/70" : "text-white/50"}`}
              >
                Management Use of Expert audit workflows
              </div>
            </div>
            {activeSection === "execution-management-use-of-expert" && (
              <ChevronRight className="h-4 w-4" />
            )}
          </button>

          {/* 29. Compliance with laws and regulations */}
          <button
            onClick={() =>
              setActiveSection("execution-compliance-with-laws-and-regulations")
            }
            className={`group flex w-full items-center gap-3 rounded-lg px-3 py-3 text-sm transition-all duration-200 ${
              activeSection === "execution-compliance-with-laws-and-regulations"
                ? "border border-[#4da3ff]/30 bg-[#4da3ff]/15 text-white shadow-lg shadow-[#4da3ff]/10"
                : "text-white/70 hover:bg-white/5 hover:text-white"
            }`}
          >
            <Calculator
              className={`h-4 w-4 ${activeSection === "execution-compliance-with-laws-and-regulations" ? "text-[#4da3ff]" : "text-white/50 group-hover:text-white"}`}
            />
            <div className="flex-1 text-left">
              <div className="font-medium">
                Compliance with laws and regulations
              </div>
              <div
                className={`text-xs ${activeSection === "execution-compliance-with-laws-and-regulations" ? "text-[#4da3ff]/70" : "text-white/50"}`}
              >
                Compliance with laws and regulations audit workflows
              </div>
            </div>
            {activeSection ===
              "execution-compliance-with-laws-and-regulations" && (
              <ChevronRight className="h-4 w-4" />
            )}
          </button>

          {/* 30. Review of estimates for management bias */}
          <button
            onClick={() =>
              setActiveSection(
                "execution-review-of-estimates-for-management-bias",
              )
            }
            className={`group flex w-full items-center gap-3 rounded-lg px-3 py-3 text-sm transition-all duration-200 ${
              activeSection ===
              "execution-review-of-estimates-for-management-bias"
                ? "border border-[#4da3ff]/30 bg-[#4da3ff]/15 text-white shadow-lg shadow-[#4da3ff]/10"
                : "text-white/70 hover:bg-white/5 hover:text-white"
            }`}
          >
            <Calculator
              className={`h-4 w-4 ${activeSection === "execution-review-of-estimates-for-management-bias" ? "text-[#4da3ff]" : "text-white/50 group-hover:text-white"}`}
            />
            <div className="flex-1 text-left">
              <div className="font-medium">
                Review of estimates for management bias
              </div>
              <div
                className={`text-xs ${activeSection === "execution-review-of-estimates-for-management-bias" ? "text-[#4da3ff]/70" : "text-white/50"}`}
              >
                Review of estimates for management bias audit workflows
              </div>
            </div>
            {activeSection ===
              "execution-review-of-estimates-for-management-bias" && (
              <ChevronRight className="h-4 w-4" />
            )}
          </button>

          {/* 31. Internal Audit */}
          <button
            onClick={() => setActiveSection("execution-internal-audit")}
            className={`group flex w-full items-center gap-3 rounded-lg px-3 py-3 text-sm transition-all duration-200 ${
              activeSection === "execution-internal-audit"
                ? "border border-[#4da3ff]/30 bg-[#4da3ff]/15 text-white shadow-lg shadow-[#4da3ff]/10"
                : "text-white/70 hover:bg-white/5 hover:text-white"
            }`}
          >
            <Calculator
              className={`h-4 w-4 ${activeSection === "execution-internal-audit" ? "text-[#4da3ff]" : "text-white/50 group-hover:text-white"}`}
            />
            <div className="flex-1 text-left">
              <div className="font-medium">Internal Audit</div>
              <div
                className={`text-xs ${activeSection === "execution-internal-audit" ? "text-[#4da3ff]/70" : "text-white/50"}`}
              >
                Internal Audit audit workflows
              </div>
            </div>
            {activeSection === "concluding-internal-audit" && (
              <ChevronRight className="h-4 w-4" />
            )}
          </button>
        </div>
      </div>
    </>
  );

  // New concluding panel that only shows proper concluding sections with search,
  // driven primarily by Sections.json from SharePoint
  const renderConcludingContentNew = () => (
    <>
      <div className="flex items-center justify-between border-b border-white/10 bg-white/5 p-4 backdrop-blur-sm">
        <div>
          <h3 className="mb-1 text-sm font-semibold text-white">
            Concluding Procedures
          </h3>
          <p className="text-xs text-white/60">
            Finalize and conclude the audit
          </p>
        </div>
        <DropdownMenu open={concludingDropdownOpen} onOpenChange={setConcludingDropdownOpen}>
          <DropdownMenuTrigger asChild>
            <Button
              size="icon"
              variant="outline"
              className="h-7 w-7 border-white/20 text-white/80 hover:bg-white/10 hover:text-white"
              title="Add Workspace"
            >
              <Plus className="h-3 w-3" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="border-white/10 bg-black/90 text-white"
          >
            <DropdownMenuItem
              onClick={() =>
                openStandardWorkspaceDialog("ConcludingProcedures")
              }
              className="cursor-pointer hover:bg-white/10"
            >
              Standard Workspace
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => openCustomWorkspaceDialog("ConcludingProcedures")}
              className="cursor-pointer hover:bg-white/10"
            >
              Custom Workspace
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => openDeleteWorkspaceDialog("ConcludingProcedures")}
              className="cursor-pointer hover:bg-white/10"
            >
              Delete Workspace
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <div className="flex min-h-0 flex-1 flex-col p-4">
        <div className="relative mb-3">
          <Search className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-white/40" />
          <Input
            type="text"
            placeholder="Search concluding items..."
            value={concludingSearchQuery}
            onChange={(e) => setConcludingSearchQuery(e.target.value)}
            className="w-full border-white/20 bg-white/5 pl-9 text-white placeholder:text-white/40 focus:border-blue-500/50 focus:ring-blue-500/20"
          />
        </div>
        <div className="max-h-[calc(100vh-260px)] flex-1 space-y-1 overflow-y-auto pr-2">
          {(() => {
            const query = concludingSearchQuery.toLowerCase();
            const matchesSearch = (title: string, description: string) =>
              !query ||
              title.toLowerCase().includes(query) ||
              description.toLowerCase().includes(query);

            const concludingConfigByTitle: Record<
              string,
              {
                section: string;
                description: string;
                icon: React.ComponentType<{ className?: string }>;
              }
            > = {
              CARO: {
                section: "concluding-caro",
                description: "Companies Auditor's Report Order",
                icon: CheckCircle,
              },
              "Audit Report & Opinion": {
                section: "concluding-audit-report-opinion",
                description: "Audit Report & Opinion",
                icon: FileText,
              },
              "Communication with those charged with governance": {
                section: "concluding-communication-governance",
                description: "Communication with those charged with governance",
                icon: Mail,
              },
              "Subsequent Events": {
                section: "concluding-subsequent-events",
                description: "Subsequent Events",
                icon: Activity,
              },
              "Obtain Written Representations": {
                section: "concluding-written-representations",
                description: "Obtain Written Representations",
                icon: Activity,
              },
              "Litigations & Claims": {
                section: "concluding-litigations-claims",
                description: "Litigations & Claims audit workflows",
                icon: Calculator,
              },
              "Compliance with laws and regulations": {
                section: "concluding-compliance-laws-regulations",
                description:
                  "Compliance with laws and regulations audit workflows",
                icon: Calculator,
              },
              "Review of estimates for management bias": {
                section: "concluding-review-estimates-bias",
                description:
                  "Review of estimates for management bias audit workflows",
                icon: Calculator,
              },
              "Internal Audit": {
                section: "concluding-internal-audit",
                description: "Internal Audit audit workflows",
                icon: Calculator,
              },
              "Going Concern": {
                section: "concluding-going-concern",
                description: "Going Concern audit workflows",
                icon: Calculator,
              },
              "Evaluate Audit Results Including Misstatements": {
                section: "concluding-evaluate-audit-results",
                description: "Evaluate Audit Results Including Misstatements",
                icon: FileText,
              },
            };

            // Only show workspaces that are present in Sections.json
            const dynamicConcludingItems =
              sectionsJson?.ConcludingProcedures &&
              typeof sectionsJson.ConcludingProcedures === "object"
                ? (
                    Object.entries(sectionsJson.ConcludingProcedures) as [
                      string,
                      string,
                    ][]
                  ).map(([title, prefix]) => {
                    const config = concludingConfigByTitle[title];
                    if (config) {
                      return { title, ...config };
                    }
                    const sectionId = `dynamic-workspace:ConcludingProcedures:${prefix}:${encodeURIComponent(
                      title,
                    )}`;
                    return {
                      title,
                      section: sectionId,
                      description: `${title} concluding procedures`,
                      icon: Calculator,
                    };
                  })
                : [];

            const concludingItems = dynamicConcludingItems || [];

            const filteredItems = concludingItems.filter((item) =>
              matchesSearch(item.title, item.description),
            );

            // Show empty state if no workspaces in Sections.json
            if (concludingItems.length === 0) {
              return (
                <div className="py-8 text-center text-sm text-white/60">
                  No concluding procedures workspaces found. Add workspaces
                  using the "+" button above.
                </div>
              );
            }

            return (
              <>
                {filteredItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = activeSection === item.section;
                  return (
                    <button
                      key={item.section}
                      onClick={() => setActiveSection(item.section)}
                      className={`group flex w-full items-center gap-3 rounded-lg px-3 py-3 text-sm transition-all duration-200 ${
                        isActive
                          ? "border border-[#4da3ff]/30 bg-[#4da3ff]/15 text-white shadow-lg shadow-[#4da3ff]/10"
                          : "text-white/70 hover:bg-white/5 hover:text-white"
                      }`}
                    >
                      <Icon
                        className={`h-4 w-4 ${
                          isActive
                            ? "text-[#4da3ff]"
                            : "text-white/50 group-hover:text-white"
                        }`}
                      />
                      <div className="flex-1 text-left">
                        <div className="font-medium">{item.title}</div>
                        <div
                          className={`text-xs ${
                            isActive ? "text-[#4da3ff]/70" : "text-white/50"
                          }`}
                        >
                          {item.description}
                        </div>
                      </div>
                      {isActive && <ChevronRight className="h-4 w-4" />}
                    </button>
                  );
                })}
                {filteredItems.length === 0 && concludingSearchQuery && (
                  <div className="py-6 text-center text-sm text-white/60">
                    No items found matching "{concludingSearchQuery}"
                  </div>
                )}
              </>
            );
          })()}
        </div>
      </div>
    </>
  );

  const renderLibrariesContent = () => (
    <>
      <div className="border-b border-white/10 bg-white/5 p-4 backdrop-blur-sm">
        <h3 className="mb-1 text-sm font-semibold text-white">Libraries</h3>
        <p className="text-xs text-white/60">
          Access various libraries and resources
        </p>
      </div>
      <div className="max-h-[calc(100vh-13rem)] flex-1 space-y-1 overflow-y-auto pr-2 pl-4">
        <div className="space-y-1">
          {visibleLibraryItems.map((library) => {
            const isActive = activeSection === library.id;
            const Icon = library.icon;
            return (
              <button
                key={library.id}
                onClick={() => setActiveSection(library.id)}
                className={`group flex w-full items-center gap-3 rounded-lg px-3 py-3 text-sm transition-all duration-200 ${
                  isActive
                    ? "border border-[#4da3ff]/30 bg-[#4da3ff]/15 text-white shadow-lg shadow-[#4da3ff]/10"
                    : "text-white/70 hover:bg-white/5 hover:text-white"
                }`}
              >
                <Icon
                  className={`h-4 w-4 ${
                    isActive
                      ? "text-[#4da3ff]"
                      : "text-white/50 group-hover:text-white"
                  }`}
                />
                <div className="flex-1 text-left">
                  <div className="font-medium">{library.title}</div>
                  <div
                    className={`text-xs ${
                      isActive ? "text-[#4da3ff]/70" : "text-white/50"
                    }`}
                  >
                    {library.description}
                  </div>
                </div>
                {isActive && <ChevronRight className="h-4 w-4" />}
              </button>
            );
          })}
        </div>
      </div>
    </>
  );

  const renderPlaceholderContent = (
    categoryTitle: string,
    categoryIcon: React.ComponentType<{ className?: string }>,
  ) => {
    const Icon = categoryIcon;
    return (
      <div className="flex flex-1 flex-col items-center justify-center p-8 text-center text-white">
        <div className="mb-4 rounded-full bg-white/5 p-6">
          <Icon className="h-12 w-12 text-white/50" />
        </div>
        <h3 className="mb-2 text-lg font-semibold text-white">
          {categoryTitle}
        </h3>
        <p className="mb-6 max-w-xs text-sm text-white/60">
          Content and features for {categoryTitle.toLowerCase()} are available
          here.
        </p>
      </div>
    );
  };

  const planningMenuItems = [
    {
      id: "engagement-acceptance",
      title: "Engagement Acceptance",
      icon: ClipboardList,
      description: "Acceptance & continuance",
    },
    {
      id: "understanding-entity",
      title: "Understanding the Entity",
      icon: Users,
      description: "Entity & environment",
    },
    {
      id: "materiality",
      title: "Materiality",
      icon: Flag,
      description: "Materiality settings",
    },
    {
      id: "preliminary-analytical",
      title: "Preliminary Analytical",
      icon: BarChart3,
      description: "Analytical procedures",
    },
    {
      id: "fraud-risk",
      title: "Fraud Risk",
      icon: Target,
      description: "Fraud risk assessment",
    },
    {
      id: "it-risk",
      title: "IT Risk",
      icon: Activity,
      description: "IT risk assessment",
    },
  ] as const;

  const renderEngagementOverviewContent = () => {
    const engagementOverviewMenuItems = [
      {
        id: "engagement-overview",
        title: "Engagement Overview",
        icon: ChartBar,
        description: "View engagement overview",
      },
    ];

    return (
      <>
        <div className="border-b border-white/10 bg-white/5 p-4 backdrop-blur-sm">
          <h3 className="mb-1 text-sm font-semibold text-white">
            Engagement Overview
          </h3>
          <p className="text-xs text-white/60">Engagement overview</p>
        </div>
        <div className="flex-1 p-4">
          <div className="space-y-1">
            {engagementOverviewMenuItems.map((item) => {
              const isActive = activeSection === item.id;
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  onClick={() => setActiveSection("engagement-overview")}
                  className={`group flex w-full items-center gap-3 rounded-lg px-3 py-3 text-sm transition-all duration-200 ${
                    isActive
                      ? "border border-[#4da3ff]/30 bg-[#4da3ff]/15 text-white shadow-lg shadow-[#4da3ff]/10"
                      : "text-white/70 hover:bg-white/5 hover:text-white"
                  }`}
                >
                  <Icon
                    className={`h-4 w-4 ${
                      isActive
                        ? "text-[#4da3ff]"
                        : "text-white/50 group-hover:text-white"
                    }`}
                  />
                  <div className="flex-1 text-left">
                    <div className="font-medium">{item.title}</div>
                    <div
                      className={`text-xs ${
                        isActive ? "text-[#4da3ff]/70" : "text-white/50"
                      }`}
                    >
                      {item.description}
                    </div>
                  </div>
                  {isActive && <ChevronRight className="h-4 w-4" />}
                </button>
              );
            })}
          </div>
        </div>
      </>
    );
  };

  const renderInternalControlNavigation = () => {
    // Extract control ID from activeSection (format: "internal-control:CONTROL_ID")
    const controlId = activeSection.startsWith("internal-control:")
      ? activeSection.split(":")[1]
      : "";

    // Internal Control sections
    const allSections = [
      {
        id: "control-summary",
        label: "Control Summary",
        number: 1,
      },
      {
        id: "evaluate-design",
        label: "Evaluate Design and Determine Implementation",
        number: 2,
      },
      {
        id: "prior-year",
        label: "Prior Year Audit Evidence",
        number: 3,
      },
      {
        id: "risk-associated",
        label: "Risk Associated with Control",
        number: 4,
      },
      {
        id: "operating-effectiveness",
        label: "Operating Effectiveness",
        number: 5,
      },
      {
        id: "rollforward",
        label: "Rollforward",
        number: 6,
      },
    ];

    // Filter out "Prior Year Audit Evidence" for Automated controls
    let filteredSections =
      isControlAutomated === true
        ? allSections.filter((section) => section.id !== "prior-year")
        : allSections;

    // Renumber sections sequentially after filtering
    const internalControlSections = filteredSections.map((section, index) => ({
      ...section,
      number: index + 1,
    }));

    // Get current section from activeSection (format: "internal-control:CONTROL_ID:section-id")
    const currentSection = activeSection.includes(":")
      ? activeSection.split(":")[2] || "control-summary"
      : "control-summary";

    return (
      <>
        <div className="border-b border-white/10 bg-white/5 p-4 backdrop-blur-sm">
          <div className="mb-2 flex items-center gap-3">
            <Shield className="h-5 w-5 text-[#4da3ff]" />
            <h3 className="text-sm font-semibold text-white">
              Internal Control
            </h3>
          </div>
          {controlId && <p className="text-xs text-white/60">{controlId}</p>}
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          <nav className="space-y-2">
            {internalControlSections.map((section, index) => {
              const isActive = currentSection === section.id;
              return (
                <button
                  key={section.id}
                  onClick={() => {
                    // Update activeSection to include the section
                    const newSection = `internal-control:${controlId}:${section.id}`;
                    setActiveSection(newSection);
                    // Also update LibrariesPage state via window
                    if ((window as any).setInternalControlSection) {
                      (window as any).setInternalControlSection(section.id);
                    }
                  }}
                  className={`w-full rounded-lg p-3 text-left text-sm transition-all ${
                    isActive
                      ? "border border-[#4da3ff]/30 bg-[#4da3ff]/20 text-[#4da3ff]"
                      : "text-white/70 hover:bg-white/10 hover:text-white"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span
                      className={`flex h-6 w-6 items-center justify-center rounded text-xs ${
                        isActive
                          ? "bg-[#4da3ff]/30 text-[#4da3ff]"
                          : "bg-white/10 text-white/60"
                      }`}
                    >
                      {section.number}
                    </span>
                    <span className="leading-tight">{section.label}</span>
                  </div>
                </button>
              );
            })}
          </nav>
        </div>
        <div className="border-t border-white/10 p-4">
          <Button
            onClick={() => {
              setActiveSection("control-library");
              if ((window as any).closeInternalControlView) {
                (window as any).closeInternalControlView();
              }
            }}
            variant="outline"
            className="w-full border-white/10 text-white hover:bg-white/10"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Control Library
          </Button>
        </div>
      </>
    );
  };

  const renderPartnerReviewContent = () => (
    <>
      <div className="border-b border-white/10 bg-white/5 p-4 backdrop-blur-sm">
        <h3 className="mb-1 text-sm font-semibold text-white">
          Partner Review
        </h3>
        <p className="text-xs text-white/60">Review and approval</p>
      </div>
      <div className="flex min-h-0 flex-1 flex-col p-4">
        <div className="max-h-[calc(100vh-260px)] flex-1 space-y-1 overflow-y-auto pr-2">
          <button
            onClick={() => setActiveSection("partner-review-dashboard")}
            className={`group flex w-full items-center gap-3 rounded-lg px-3 py-3 text-sm transition-all duration-200 ${
              activeSection === "partner-review-dashboard"
                ? "border border-[#4da3ff]/30 bg-[#4da3ff]/15 text-white shadow-lg shadow-[#4da3ff]/10"
                : "text-white/70 hover:bg-white/5 hover:text-white"
            }`}
          >
            <LayoutDashboard
              className={`h-4 w-4 ${activeSection === "partner-review-dashboard" ? "text-[#4da3ff]" : "text-white/50 group-hover:text-white"}`}
            />
            <div className="flex-1 text-left">
              <div className="font-medium">Partner Overview</div>
              <div
                className={`text-xs ${activeSection === "partner-review-dashboard" ? "text-[#4da3ff]/70" : "text-white/50"}`}
              >
                Dashboard summary and metrics
              </div>
            </div>
            {activeSection === "partner-review-dashboard" && (
              <ChevronRight className="h-4 w-4" />
            )}
          </button>
          <button
            onClick={() => setActiveSection("partner-review-planning")}
            className={`group flex w-full items-center gap-3 rounded-lg px-3 py-3 text-sm transition-all duration-200 ${
              activeSection === "partner-review-planning"
                ? "border border-[#4da3ff]/30 bg-[#4da3ff]/15 text-white shadow-lg shadow-[#4da3ff]/10"
                : "text-white/70 hover:bg-white/5 hover:text-white"
            }`}
          >
            <FileCheck
              className={`h-4 w-4 ${activeSection === "partner-review-planning" ? "text-[#4da3ff]" : "text-white/50 group-hover:text-white"}`}
            />
            <div className="flex-1 text-left">
              <div className="font-medium">Planning Review</div>
              <div
                className={`text-xs ${activeSection === "partner-review-planning" ? "text-[#4da3ff]/70" : "text-white/50"}`}
              >
                Partner review of planning phase
              </div>
            </div>
            {activeSection === "partner-review-planning" && (
              <ChevronRight className="h-4 w-4" />
            )}
          </button>
          <button
            onClick={() => setActiveSection("partner-review-concluding")}
            className={`group flex w-full items-center gap-3 rounded-lg px-3 py-3 text-sm transition-all duration-200 ${
              activeSection === "partner-review-concluding"
                ? "border border-[#4da3ff]/30 bg-[#4da3ff]/15 text-white shadow-lg shadow-[#4da3ff]/10"
                : "text-white/70 hover:bg-white/5 hover:text-white"
            }`}
          >
            <ClipboardCheck
              className={`h-4 w-4 ${activeSection === "partner-review-concluding" ? "text-[#4da3ff]" : "text-white/50 group-hover:text-white"}`}
            />
            <div className="flex-1 text-left">
              <div className="font-medium">Concluding Procedure Review</div>
              <div
                className={`text-xs ${activeSection === "partner-review-concluding" ? "text-[#4da3ff]/70" : "text-white/50"}`}
              >
                Partner review of concluding procedures
              </div>
            </div>
            {activeSection === "partner-review-concluding" && (
              <ChevronRight className="h-4 w-4" />
            )}
          </button>
        </div>
      </div>
    </>
  );

  const renderEqrReviewContent = () => (
    <>
      <div className="border-b border-white/10 bg-white/5 p-4 backdrop-blur-sm">
        <h3 className="mb-1 text-sm font-semibold text-white">EQR Review</h3>
        <p className="text-xs text-white/60">Engagement quality review</p>
      </div>
      <div className="flex min-h-0 flex-1 flex-col p-4">
        <div className="max-h-[calc(100vh-260px)] flex-1 space-y-1 overflow-y-auto pr-2">
          <button
            onClick={() => setActiveSection("eqr-dashboard")}
            className={`group flex w-full items-center gap-3 rounded-lg px-3 py-3 text-sm transition-all duration-200 ${
              activeSection === "eqr-dashboard"
                ? "border border-[#4da3ff]/30 bg-[#4da3ff]/15 text-white shadow-lg shadow-[#4da3ff]/10"
                : "text-white/70 hover:bg-white/5 hover:text-white"
            }`}
          >
            <LayoutDashboard
              className={`h-4 w-4 ${
                activeSection === "eqr-dashboard"
                  ? "text-[#4da3ff]"
                  : "text-white/50 group-hover:text-white"
              }`}
            />
            <div className="flex-1 text-left">
              <div className="font-medium">EQR Dashboard</div>
              <div
                className={`text-xs ${
                  activeSection === "eqr-dashboard"
                    ? "text-[#4da3ff]/70"
                    : "text-white/50"
                }`}
              >
                Overview of EQR review
              </div>
            </div>
            {activeSection === "eqr-dashboard" && (
              <ChevronRight className="h-4 w-4" />
            )}
          </button>
          <button
            onClick={() => setActiveSection("eqr-checklist")}
            className={`group flex w-full items-center gap-3 rounded-lg px-3 py-3 text-sm transition-all duration-200 ${
              activeSection === "eqr-checklist"
                ? "border border-[#4da3ff]/30 bg-[#4da3ff]/15 text-white shadow-lg shadow-[#4da3ff]/10"
                : "text-white/70 hover:bg-white/5 hover:text-white"
            }`}
          >
            <ClipboardCheck
              className={`h-4 w-4 ${
                activeSection === "eqr-checklist"
                  ? "text-[#4da3ff]"
                  : "text-white/50 group-hover:text-white"
              }`}
            />
            <div className="flex-1 text-left">
              <div className="font-medium">EQR Checklist</div>
              <div
                className={`text-xs ${
                  activeSection === "eqr-checklist"
                    ? "text-[#4da3ff]/70"
                    : "text-white/50"
                }`}
              >
                Checklist for EQR procedures
              </div>
            </div>
            {activeSection === "eqr-checklist" && (
              <ChevronRight className="h-4 w-4" />
            )}
          </button>
        </div>
      </div>
    </>
  );

  const renderCategoryContent = () => {
    // Check if we're viewing an internal control
    if (activeSection.startsWith("internal-control:")) {
      return renderInternalControlNavigation();
    }

    const category = mainCategories.find((cat) => cat.id === activeCategory);

    switch (activeCategory) {
      case "engagement-overview":
        return renderEngagementOverviewContent();
      case "financial-data":
        return renderFinancialDataContent();
      case "planning-overview":
        return renderPlanningContent();
      case "execution":
        return renderExecutionContent();
      case "concluding":
        return renderConcludingContentNew();
      case "partner-review":
        return renderPartnerReviewContent();
      case "eqr-review":
        return renderEqrReviewContent();
      case "libraries":
        return renderLibrariesContent();
      default:
        return renderPlaceholderContent(
          category?.title || "Section",
          category?.icon || Home,
        );
    }
  };

  return (
    <div className="flex h-screen min-h-0 border-r border-white/10 bg-black/60 backdrop-blur-lg">
      {/* Icon strip */}
      <div className="flex h-full w-16 shrink-0 flex-col items-center space-y-3 overflow-y-auto overscroll-contain border-r border-white/10 bg-gradient-to-b from-black to-black/90 py-6">
        <div className="mb-4 -rotate-90 transform text-xs font-semibold whitespace-nowrap text-white/40">
          MENU
        </div>
        <div className="flex flex-1 flex-col items-center space-y-3">
          {mainCategories
            .map((category) => {
              const Icon = category.icon;
              const isActive = activeCategory === category.id;
              return (
                <HoverCard key={category.id}>
                  <HoverCardTrigger asChild>
                    <button
                      onClick={() => {
                        setActiveCategory(category.id);
                        // Reset active section when switching categories
                        if (category.id === "engagement-overview") {
                          setActiveSection("engagement-overview");
                        } else if (category.id === "financial-data") {
                          setActiveSection("overview");
                        } else if (category.id === "libraries") {
                          setActiveSection("libraries");
                        } else if (category.id === "planning-overview") {
                          setActiveSection("planning-overview");
                        } else if (category.id === "execution") {
                          // Show a neutral Execution landing page; user must pick a workspace explicitly
                          setActiveSection("execution");
                        } else if (category.id === "concluding") {
                          // Show a neutral Conclusion landing page; user must pick a workspace explicitly
                          setActiveSection("concluding");
                        } else if (category.id === "partner-review") {
                          setActiveSection("partner-review-dashboard");
                        } else if (category.id === "eqr-review") {
                          setActiveSection("eqr-dashboard");
                        }
                      }}
                      className={`rounded-lg p-2 transition-all duration-300 ${
                        isActive
                          ? "border border-[#4da3ff]/40 bg-[#4da3ff]/20 text-[#4da3ff] shadow-lg shadow-[#4da3ff]/20"
                          : "text-white/60 hover:bg-white/10 hover:text-white"
                      }`}
                    >
                      <Icon className="h-4 w-4" />
                    </button>
                  </HoverCardTrigger>
                  <HoverCardContent
                    side="right"
                    sideOffset={10}
                    className="w-auto border-white/10 bg-black/90 px-3 py-1.5 text-white shadow-xl shadow-black/40 backdrop-blur-md"
                  >
                    <p className="text-xs font-medium">{category.title}</p>
                  </HoverCardContent>
                </HoverCard>
              );
            })}
        </div>
        <div className="mt-4">
          <button
            onClick={() => setIsCollapsed((v) => !v)}
            className="rounded-lg p-2 text-white/60 transition-colors hover:bg-white/10 hover:text-white"
            title={isCollapsed ? "Expand panel" : "Collapse panel"}
            aria-label={isCollapsed ? "Expand panel" : "Collapse panel"}
          >
            {isCollapsed ? (
              <ChevronRight className="h-4 w-4" />
            ) : (
              <ChevronLeft className="h-4 w-4" />
            )}
          </button>
        </div>
      </div>

      {/* Left Panel Content */}
      <div
        className={`flex min-h-0 flex-col bg-black/40 text-white backdrop-blur-xl transition-all duration-300 ${
          isCollapsed
            ? "pointer-events-none w-0 opacity-0"
            : "h-full w-72 overflow-y-hidden"
        }`}
      >
        {!isCollapsed && renderCategoryContent()}
      </div>

      {/* Add Custom Workspace Dialog */}
      <Dialog open={addWorkspaceOpen} onOpenChange={setAddWorkspaceOpen}>
        <DialogContent 
          className="max-w-md"
          onPointerDownOutside={(e) => {
            // Prevent immediate closing from dropdown menu interaction
            const target = e.target as HTMLElement;
            if (target.closest('[role="menu"]') || target.closest('[data-radix-popper-content-wrapper]')) {
              e.preventDefault();
            }
          }}
        >
          <DialogHeader>
            <DialogTitle>Add Custom Workspace</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1">
              <label className="text-xs font-medium text-white/80">
                Workspace Name
              </label>
              <Input
                value={newWorkspaceName}
                onChange={(e) => setNewWorkspaceName(e.target.value)}
                placeholder="e.g. New Area Name"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-white/80">
                Workspace ID (prefix)
              </label>
              <Input
                value={newWorkspaceId}
                onChange={(e) => setNewWorkspaceId(e.target.value)}
                placeholder="e.g. NEW"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-white/80">
                Workspace Area
              </label>
              <Select
                value={newWorkspaceArea}
                onValueChange={(val) =>
                  setNewWorkspaceArea(
                    val as "Planning" | "Execution" | "ConcludingProcedures",
                  )
                }
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Planning">Planning</SelectItem>
                  <SelectItem value="Execution">Execution</SelectItem>
                  <SelectItem value="ConcludingProcedures">
                    Concluding Procedures
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setAddWorkspaceOpen(false)}
              disabled={isAddingWorkspace}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddWorkspaceSubmit}
              disabled={isAddingWorkspace}
            >
              {isAddingWorkspace ? "Adding..." : "Add Workspace"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Standard Workspace Dialog */}
      <Dialog open={standardWorkspaceOpen} onOpenChange={setStandardWorkspaceOpen}>
        <DialogContent 
          className="max-w-2xl max-h-[80vh]"
          onPointerDownOutside={(e) => {
            // Prevent immediate closing from dropdown menu interaction
            const target = e.target as HTMLElement;
            if (target.closest('[role="menu"]') || target.closest('[data-radix-popper-content-wrapper]')) {
              e.preventDefault();
            }
          }}
        >
          <DialogHeader>
            <DialogTitle>Add Standard Workspace</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1">
              <label className="text-xs font-medium text-white/80">
                Select Area
              </label>
              <Select
                value={selectedAreaForStandard}
                onValueChange={(val) => {
                  setSelectedAreaForStandard(
                    val as "Planning" | "Execution" | "ConcludingProcedures",
                  );
                  setSelectedStandardWorkspaces(new Set());
                }}
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Planning">Planning</SelectItem>
                  <SelectItem value="Execution">Execution</SelectItem>
                  <SelectItem value="ConcludingProcedures">
                    Concluding Procedures
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="max-h-[50vh] space-y-2 overflow-y-auto">
              <label className="text-xs font-medium text-white/80">
                Available Standard Workspaces
              </label>
              <div className="space-y-2">
                {(() => {
                  // Get standard workspaces for selected area
                  const availableWorkspaces = STANDARD_WORKSPACES.filter(
                    (ws) =>
                      ws.area === selectedAreaForStandard &&
                      isWorkspaceAllowedForFrf(ws.name, engagementFrf) &&
                      !shouldHideCaroFromConcludingStandardList(
                        ws,
                        selectedAreaForStandard,
                        isLimitedReviewExecution,
                      ),
                  );

                  // Get already added workspaces from Sections.json
                  const addedWorkspaces = new Set<string>();
                  if (sectionsJson) {
                    const areaData = sectionsJson[selectedAreaForStandard];
                    if (areaData && typeof areaData === "object") {
                      Object.keys(areaData).forEach((name) =>
                        addedWorkspaces.add(name),
                      );
                    }
                  }

                  // Filter out already added workspaces
                  const unaddedWorkspaces = availableWorkspaces.filter(
                    (ws) => !addedWorkspaces.has(ws.name),
                  );

                  if (unaddedWorkspaces.length === 0) {
                    return (
                      <div className="py-8 text-center text-sm text-white/60">
                        All standard workspaces for {selectedAreaForStandard}{" "}
                        have been added.
                      </div>
                    );
                  }

                  return unaddedWorkspaces.map((workspace) => {
                    const isSelected = selectedStandardWorkspaces.has(
                      workspace.name,
                    );
                    return (
                      <div
                        key={`${workspace.area}-${workspace.name}`}
                        className={`flex cursor-pointer items-center justify-between rounded-md border p-3 transition-colors ${
                          isSelected
                            ? "border-blue-500/50 bg-blue-500/10"
                            : "border-white/10 bg-black/20 hover:bg-white/5"
                        }`}
                        onClick={() => {
                          const newSet = new Set(selectedStandardWorkspaces);
                          if (isSelected) {
                            newSet.delete(workspace.name);
                          } else {
                            newSet.add(workspace.name);
                          }
                          setSelectedStandardWorkspaces(newSet);
                        }}
                      >
                        <div className="flex flex-1 items-center gap-3">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => {
                              const newSet = new Set(
                                selectedStandardWorkspaces,
                              );
                              if (isSelected) {
                                newSet.delete(workspace.name);
                              } else {
                                newSet.add(workspace.name);
                              }
                              setSelectedStandardWorkspaces(newSet);
                            }}
                            className="h-4 w-4 rounded border-white/20 bg-black/40 text-blue-500 focus:ring-blue-500"
                            onClick={(e) => e.stopPropagation()}
                          />
                          <div className="flex-1">
                            <div className="text-sm font-medium text-white">
                              {workspace.name}
                            </div>
                            <div className="mt-1 text-xs text-white/60">
                              Prefix: {workspace.prefix}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setStandardWorkspaceOpen(false);
                setSelectedStandardWorkspaces(new Set());
              }}
              disabled={isAddingWorkspace}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddStandardWorkspaces}
              disabled={
                isAddingWorkspace || selectedStandardWorkspaces.size === 0
              }
            >
              {isAddingWorkspace
                ? "Adding..."
                : `Add Selected (${selectedStandardWorkspaces.size})`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Workspace Dialog */}
      <Dialog open={deleteWorkspaceOpen} onOpenChange={setDeleteWorkspaceOpen}>
        <DialogContent 
          className="max-w-2xl max-h-[80vh]"
          onPointerDownOutside={(e) => {
            // Prevent immediate closing from dropdown menu interaction
            const target = e.target as HTMLElement;
            if (target.closest('[role="menu"]') || target.closest('[data-radix-popper-content-wrapper]')) {
              e.preventDefault();
            }
          }}
        >
          <DialogHeader>
            <DialogTitle>Delete Workspace</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1">
              <label className="text-xs font-medium text-white/80">
                Select Workspaces to Delete
              </label>
              <div className="max-h-[50vh] space-y-2 overflow-y-auto rounded-md border border-white/10 p-4">
                {(() => {
                  const areaData = sectionsJson?.[deleteWorkspaceArea];
                  if (
                    !areaData ||
                    typeof areaData !== "object" ||
                    Object.keys(areaData).length === 0
                  ) {
                    return (
                      <div className="py-8 text-center text-sm text-white/60">
                        No workspaces found in {deleteWorkspaceArea}.
                      </div>
                    );
                  }

                  const workspaceEntries = Object.entries(areaData) as [
                    string,
                    string,
                  ][];

                  return workspaceEntries.map(
                    ([workspaceName, workspaceId]) => {
                      const isSelected =
                        selectedWorkspacesToDelete.has(workspaceName);
                      return (
                        <div
                          key={workspaceName}
                          className={`flex cursor-pointer items-center justify-between rounded-md border p-3 transition-colors ${
                            isSelected
                              ? "border-blue-500/50 bg-blue-500/10"
                              : "border-white/10 bg-black/20 hover:bg-white/5"
                          }`}
                          onClick={() => {
                            const newSet = new Set(selectedWorkspacesToDelete);
                            if (isSelected) {
                              newSet.delete(workspaceName);
                            } else {
                              newSet.add(workspaceName);
                            }
                            setSelectedWorkspacesToDelete(newSet);
                          }}
                        >
                          <div className="flex flex-1 items-center gap-3">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => {
                                const newSet = new Set(
                                  selectedWorkspacesToDelete,
                                );
                                if (isSelected) {
                                  newSet.delete(workspaceName);
                                } else {
                                  newSet.add(workspaceName);
                                }
                                setSelectedWorkspacesToDelete(newSet);
                              }}
                              className="h-4 w-4 rounded border-white/20 bg-black/40 text-blue-500 focus:ring-blue-500"
                            />
                            <div className="flex-1">
                              <div className="text-sm font-medium text-white">
                                {workspaceName}
                              </div>
                              <div className="mt-1 text-xs text-white/60">
                                Prefix: {workspaceId}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    },
                  );
                })()}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setDeleteWorkspaceOpen(false);
                setSelectedWorkspacesToDelete(new Set());
              }}
              disabled={isDeletingWorkspace}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteWorkspaces}
              disabled={
                isDeletingWorkspace || selectedWorkspacesToDelete.size === 0
              }
              variant="destructive"
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmationOpen}
        onOpenChange={setDeleteConfirmationOpen}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Confirm Deletion</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-white/80">
              Are you sure you want to delete the following{" "}
              {selectedWorkspacesToDelete.size} workspace(s)?
            </p>
            <div className="max-h-[30vh] space-y-2 overflow-y-auto rounded-md border border-white/10 bg-black/20 p-3">
              {Array.from(selectedWorkspacesToDelete).map((workspaceName) => (
                <div key={workspaceName} className="text-sm text-white/90">
                  • {workspaceName}
                </div>
              ))}
            </div>
            <p className="text-xs text-red-400/80">
              This action cannot be undone.
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteConfirmationOpen(false)}
              disabled={isDeletingWorkspace}
            >
              Cancel
            </Button>
            <Button
              onClick={confirmDeleteWorkspaces}
              disabled={isDeletingWorkspace}
              variant="destructive"
            >
              {isDeletingWorkspace ? "Deleting..." : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
